import type {
  AttackCollider,
  AttackProfile,
  AttackVariety,
  Creature,
  DamageModifiers,
  EntityId,
  Fish,
} from '../types';
import { TOLERATE } from '../types';
import { mods, dmg, dropEntry, dropTrophy } from '../model/game';
import { spawner } from '../model/spawner';

const defaultDmgModifiers: DamageModifiers = {
  blunt: 'normal',
  slash: 'normal',
  pierce: 'normal',
  chop: 'ignore',
  pickaxe: 'ignore',
  fire: 'normal',
  frost: 'normal',
  lightning: 'normal',
  poison: 'normal',
  spirit: 'normal',
};

const animalDmgModifiers: DamageModifiers = {
  ...defaultDmgModifiers,
  spirit: 'immune',
}

const grayModifiers: DamageModifiers = {
  ...animalDmgModifiers,
  fire: 'veryWeak',
  poison: 'resistant',
};

const blobDamageModifiers: DamageModifiers = mods([2, 1, 1, 4, 4, 1, 2, 2, 3, 0]);

const skeletonDamageModifiers: DamageModifiers = {
  ...defaultDmgModifiers,
  blunt: 'weak',
  pierce: 'resistant',
  fire: 'weak',
  frost: 'resistant',
  poison: 'immune',
};

const loxDamageModifiers = mods([1, 1, 0, 4, 4, 2, 1, 0, 0, 3]);

const seekerDamageModifiers: DamageModifiers = {
  blunt: 'resistant',
  slash: 'resistant',
  pierce: 'resistant',
  chop: 'ignore',
  pickaxe: 'ignore',
  fire: 'normal',
  frost: 'normal',
  lightning: 'normal',
  poison: 'normal',
  spirit: 'immune',
};

const charredDmgModifiers = mods([0, 0, 1, 4, 4, 5, 0, 0, 3, 2]);
const charredSummonDmgModifiers = mods([0, 0, 1, 4, 4, 3, 0, 0, 3, 2]);

const unblockable = true;
const undodgeable = true;

const areaCollider = (radius: number): AttackCollider => ({ type: 'area', radius });

const single = (attacks: AttackProfile[]): [AttackVariety] => {
  return [{ rate: 1, variety: '', attacks }]; 
};

export const maxLvl = (creature: Creature | Fish) => {
  return creature.spawners.reduce((l, s) => Math.max(l, s.levels[1]), (creature as Creature).maxLvl ?? 1);
};

export const minLvl = (creature: Creature | Fish) => {
  return creature.spawners.reduce((l, s) => Math.min(l, s.levels[1]), (creature as Creature).minLvl ?? 1);
};

function variations(main: Creature, ...others: (Partial<Omit<Creature, 'id'>> & { id: EntityId })[]): Creature[] {
  return [
    main,
    ...others.map(other => ({ ...main, ...other })),
  ];
}

const aggravatable = true;

export const player: Creature = {
  type: 'creature',
  id: 'Player',
  ragdollId: 'Player_ragdoll',
  components: ['Character'],
  tier: 0,
  emoji: '🧑',
  faction: 'Players',
  spawners: [],
  attacks: [],
  tolerate: TOLERATE.WATER,
  speed: {
    walk: 1.6,
    run: 7,
    swim: 2,
  },
  turnSpeed: {
    walk: 300,
    run: 3000,
    swim: 100,
  },
  hp: 100,
  stagger: null,
  damageModifiers: mods([0, 0, 0, 3, 3, 0, 0, 0, 0, 3]),
  drop: [],
}

export const creatures: Creature[] = [
// MEADOWS
  {
    type: 'creature',
    id: 'Deer',
    ragdollId: 'deer_ragdoll',
    components: ['Character'],
    tags: ['animal'],
    tier: 1,
    emoji: '🦌',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 0,
      biomes: ['Meadows', 'BlackForest'],
      maxSpawned: 4,
      interval: 100,
      chance: 0.5,
      distance: 64,
      envs: [],
      groupSize: [1, 3],
      groupRadius: 5,
      forest: true,
    })],
    attacks: [],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.5,
      run: 7,
      swim: 2,
    },
    turnSpeed: {
      walk: 80,
      run: 200,
      swim: 80,
    },
    hp: 10,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('DeerMeat', { min: 2, max: 2 }),
      dropEntry('DeerHide', { max: 3 }),
      dropTrophy('TrophyDeer', 0.5),
    ],
  },
  {
    type: 'creature',
    group: 'bird',
    id: 'Seagal',
    ragdollId: null,
    components: ['RandomFlyingBird'],
    tags: ['fly'],
    tier: 1,
    emoji: '🦆',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 0,
      biomes: ['Meadows', 'BlackForest', 'Plains', 'Ocean'],
      maxSpawned: 5,
      interval: 120,
      chance: 0.5,
      distance: 51,
      groupSize: [1, 2],
      groupRadius: 2,
      altitude: [-4, 0.2],
      levels: [1, 1],
    })],
    attacks: [],
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 0,
      run: 15,
      swim: 0,
    },
    turnSpeed: {
      walk: 0,
      run: 60,
      swim: 0,
    },
    hp: 1,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Feathers', { min: 3, max: 3 }),
    ],
  },
  {
    type: 'creature',
    id: 'Greyling',
    ragdollId: 'Greyling_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 1,
    emoji: '🐀',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 0,
      biomes: ['Meadows'],
      maxSpawned: 2,
      interval: 300,
      chance: 0.5,
      distance: 30,
      levels: [1, 1],
    })],
    // ['Meadows'],
    attacks: single([{ dmg: dmg({ slash: 5 }), stagger: 1.94, name: 'bite' }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 0,
      run: 0,
      swim: 0,
    },
    turnSpeed: {
      walk: 0,
      run: 0,
      swim: 0,
    },
    hp: 20,
    stagger: {
      factor: 0.3,
      time: 1.94,
    },
    damageModifiers: grayModifiers,
    drop: [
      dropEntry('Resin'),
    ],
  },
  {
    type: 'creature',
    id: 'Neck',
    ragdollId: 'Neck_Ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal'],
    tier: 1,
    upgradeDistance: 800,
    emoji: '🦎',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 0,
      biomes: ['Meadows'],
      maxSpawned: 6,
      interval: 100,
      chance: 0.5,
      distance: 5,
      groupSize: [2, 4],
      groupRadius: 5,
      altitude: [-1.5, 0.5],
      minDistance: 800,
    }), spawner({
      tier: 0,
      biomes: ['Meadows'],
      maxSpawned: 8,
      interval: 30,
      chance: 1,
      distance: 32,
      envs: ['Rain', 'ThunderStorm', 'LightRain'],
      groupSize: [2, 4],
      groupRadius: 5,
      altitude: [0, 10],
      minDistance: 800,
    })],
    attacks: single([{ dmg: dmg({ slash: 6 }), stagger: 1.66, name: 'bite' }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 5,
      swim: 2,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 400,
    },
    hp: 5,
    stagger: {
      factor: 0.5,
      time: 1.54,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('NeckTail', { chance: 0.75 }),
      dropTrophy('TrophyNeck', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Boar',
    ragdollId: 'boar_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Procreation', 'Tameable'],
    tags: ['animal'],
    tier: 1,
    upgradeDistance: 800,
    emoji: '🐗',
    faction: 'ForestMonsters',
    factionGroup: 'boar',
    spawners: [spawner({
      tier: 0,
      biomes: ['Meadows'],
      biomeAreas: 7,
      maxSpawned: 4,
      interval: 150,
      chance: 0.5,
      distance: 64,
      groupSize: [1, 3],
      groupRadius: 5,
      minDistance: 800,
    })],
    attacks: single([{ dmg: dmg({ blunt: 10 }), stagger: 1.24, name: 'tusks' }]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.5,
      run: 8,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 200,
      swim: 100,
    },
    hp: 10,
    stagger: {
      factor: 0.5,
      time: 1.24,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('RawMeat'),
      dropEntry('LeatherScraps'),
      dropTrophy('TrophyBoar', 0.15),
    ],
    tame: { tameTime: 1800, fedTime: 600, commandable: true,
            eats: ['Raspberry', 'Mushroom', 'Blueberries', 'Carrot', 'Turnip', 'Onion'] },
            // eatRange:1.0, searchRange:10, heal:5
    pregnancy: { points: 3, time: 60, chance: 0.33, grow: 3000, childId: 'Boar_piggy' },
  },
  {
    type: 'creature',
    id: 'Boar_piggy',
    iconId: 'creature/Boar',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Growup'],
    tags: ['animal'],
    tier: 1,
    emoji: '🐗',
    faction: 'ForestMonsters',
    factionGroup: 'boar',
    spawners: [],
    attacks: [],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.5,
      run: 8,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 200,
      swim: 100,
    },
    hp: 10,
    stagger: {
      factor: 0.5,
      time: 1,
    },
    damageModifiers: animalDmgModifiers,
    drop: [],
  },
  {
    type: 'creature',
    id: 'Eikthyr',
    ragdollId: 'eikthyr_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 1,
    emoji: '🦌',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      { dmg: dmg({ pierce: 20, chop: 1000, pickaxe: 1000 }), name: 'antlers', force: 100, toolTier: 0 },
      { dmg: dmg({ lightning: 15 }), name: 'charge', force: 200 },
      { dmg: dmg({ lightning: 15 }), name: 'stomp', force: 10, collider: areaCollider(10) },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 8,
      swim: 4,
    },
    turnSpeed: {
      walk: 100,
      run: 100,
      swim: 50,
    },
    hp: 500,
    stagger: null,
    damageModifiers: defaultDmgModifiers,
    drop: [
      dropEntry('HardAntler', { min: 3, max: 3 }),
      dropTrophy('TrophyEikthyr', 1),
    ],
  },
// FOREST
  {
    type: 'creature',
    group: 'bird',
    id: 'Crow',
    ragdollId: null,
    components: ['RandomFlyingBird'],
    tags: ['fly'],
    tier: 2,
    emoji: '🐦',
    faction: 'ForestMonsters',
    spawners: [],
    attacks: [],
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 0,
      run: 15,
      swim: 0,
    },
    turnSpeed: {
      walk: 0,
      run: 60,
      swim: 0,
    },
    hp: 1,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Feathers', { min: 3, max: 3 }),
    ],
  },
  {
    type: 'creature',
    id: 'Skeleton',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 2,
    emoji: '💀',
    faction: 'Undead',
    spawners: [spawner({
      tier: 4,
      biomes: ['Swamp'],
      maxSpawned: 4,
      interval: 400,
      distance: 15,
      chance: 0.25,
      groupSize: [2, 4],
      altitude: [-1, 10],
    }), spawner({
      tier: 4,
      biomes: ['Meadows', 'BlackForest', 'Swamp', 'Mountain', 'Plains'],
      maxSpawned: 3,
      interval: 300,
      chance: 0.25,
      killed: 'Bonemass',
      groupSize: [1, 3],
      night: true,
      levels: [1, 1],
    })],
    attacks: [
      { rate: 4, variety: 'sword', attacks: [{ dmg: dmg({ slash: 25 }), stagger: 2.48, name: 'sword' }] },
      { rate: 1, variety: 'bow', attacks: [{ dmg: dmg({ pierce: 20 }), stagger: 2.48, name: 'bow' }] },
    ],
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 4,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 300,
    },
    hp: 40,
    stagger: {
      factor: 0.5,
      time: 2.48,
    },
    damageModifiers: skeletonDamageModifiers,
    drop: [
      dropEntry('BoneFragments'),
      dropTrophy('TrophySkeleton', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Skeleton_Hildir',
    iconId: 'resource/TrophySkeletonHildir',
    group: 'semiboss',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 2,
    emoji: '☠️',
    faction: 'Undead',
    spawners: [],
    attacks: single([
      { dmg: dmg({ fire: 75 }), name: 'skeleton_hildir_firenova', force: 40, toolTier: 0 }, // AoE
      { dmg: dmg({ slash: 60, fire: 20 }), name: 'skeleton_sword_hildir', force: 40, toolTier: 0 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE | TOLERATE.FIRE,
    speed: {
      walk: 1,
      run: 4,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 100,
    },
    hp: 1200,
    stagger: {
      factor: 0.5,
      time: 2.48,
    },
    damageModifiers: mods([2, 0, 1, 4, 4, 3, 2, 0, 3, 0 ]),
    drop: [
      dropTrophy('chest_hildir1', 1),
      dropTrophy('TrophySkeletonHildir', 1),
    ],
  },
  {
    type: 'creature',
    id: 'Skeleton_Hildir_nochest',
    iconId: 'resource/TrophySkeletonHildir',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 3,
    emoji: '☠️',
    faction: 'Undead',
    spawners: [],
    attacks: single([
      { dmg: dmg({ fire: 75 }), name: 'skeleton_hildir_firenova', force: 40, toolTier: 0 }, // AoE
      { dmg: dmg({ slash: 60, fire: 20 }), name: 'skeleton_sword_hildir', force: 40, toolTier: 0 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE | TOLERATE.FIRE,
    speed: {
      walk: 1,
      run: 4,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 100,
    },
    hp: 600,
    stagger: {
      factor: 0.5,
      time: 2.48,
    },
    damageModifiers: mods([2, 0, 1, 4, 4, 3, 2, 0, 3, 0 ]),
    drop: [
      dropTrophy('TrophySkeletonHildir', 1),
    ],
  },
  {
    type: 'creature',
    id: 'Skeleton_Poison', // Rancid Remains
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 2,
    emoji: '☠️',
    faction: 'Undead',
    spawners: [],
    attacks: single([{
      dmg: dmg({ blunt: 20, poison: 30 }),
      stagger: 3.96, 
      name: 'mace',
    }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 4,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 300,
    },
    hp: 100,
    stagger: {
      factor: 0.5,
      time: 3.44,
    },
    damageModifiers: skeletonDamageModifiers,
    drop: [
      dropEntry('BoneFragments', { min: 3, max: 3 }),
      dropTrophy('TrophySkeletonPoison', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Ghost',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 2,
    emoji: '👻',
    faction: 'Undead',
    spawners: [],
    attacks: single([ { dmg: dmg({ slash: 25 }), stagger: 1.64, name: 'slash' } ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 4,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 300,
    },
    hp: 60,
    stagger: {
      factor: 0.5,
      time: 1.64,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      blunt: 'resistant',
      slash: 'resistant',
      pierce: 'resistant',
      poison: 'immune',
      spirit: 'weak',
    },
    drop: [
      dropEntry('Ectoplasm', { max: 3 }),
      dropTrophy('TrophyGhost', 0.1),
    ],
  },
  {
    type: 'creature',
    group: 'gray',
    id: 'Greydwarf',
    ragdollId: 'Greydwarf_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 2,
    emoji: '',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 1,
      biomes: ['BlackForest'],
      maxSpawned: 3,
      interval: 120,
      chance: 0.3,
      groupSize: [2, 3],
      night: false,
    }), spawner({
      tier: 2,
      biomes: ['Meadows'],
      maxSpawned: 1,
      interval: 120,
      chance: 0.3,
      killed: 'Eikthyr',
      groupSize: [2, 3],
      night: true,
      forest: true,
      levels: [1, 1],
    }), spawner({
      tier: 2,
      biomes: ['BlackForest'],
      biomeAreas: 7,
      maxSpawned: 6,
      interval: 60,
      chance: 1,
      groupSize: [3, 4],
      groupRadius: 10,
      night: true,
    }), spawner({
      tier: 2,
      biomes: ['Meadows'],
      maxSpawned: 2,
      interval: 200,
      chance: 0.5,
      distance: 20,
      killed: 'Eikthyr',
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 14 }), stagger: 1.94, name: 'hit' },
      { dmg: dmg({ blunt: 10 }), stagger: 1.94, name: 'stone' },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 40,
    stagger: {
      factor: 0.3,
      time: 1.94,
    },
    damageModifiers: grayModifiers,
    drop: [
      dropEntry('GreydwarfEye', { chance: 0.5 }),
      dropEntry('Stone'),
      dropEntry('Wood'),
      dropEntry('Resin'),
      dropTrophy('TrophyGreydwarf', 0.05),
    ],
  },
  {
    type: 'creature',
    group: 'gray',
    id: 'Greydwarf_Shaman',
    ragdollId: 'Greydwarf_Shaman_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 2,
    emoji: '',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 2,
      biomes: ['BlackForest'],
      maxSpawned: 2,
      interval: 60,
      chance: 0.506,
      groupRadius: 10,
      night: true,
      levels: [1, 3],
    })],
    attacks: single([
      { dmg: dmg({ poison: 30 }), name: 'poison breath' },
      { dmg: dmg({ slash: 14 }), stagger: 1.12, name: 'slash' },
      { cast: 'SE_Greydwarf_shaman_heal' }
    ]),
    // heals for 2.5hp/s for 4s in 4.3m radius
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 4,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 60,
    stagger: {
      factor: 0.33,
      time: 1.12,
    },
    damageModifiers: grayModifiers,
    drop: [
      dropEntry('GreydwarfEye', { chance: 0.5 }),
      dropEntry('Wood'),
      dropEntry('Resin', { max: 2 }),
      dropTrophy('TrophyGreydwarfShaman', 0.1),
      dropEntry('Pukeberries', { max: 2 }),
    ],
  },
  {
    type: 'creature',
    group: 'gray',
    id: 'Greydwarf_Elite',
    ragdollId: 'Greydwarf_elite_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 2,
    upgradeDistance: 2000,
    emoji: '',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 2,
      biomes: ['BlackForest'],
      maxSpawned: 1,
      interval: 120,
      chance: 0.506,
      groupRadius: 10,
      night: true,
      minDistance: 2000,
    }), spawner({
      tier: 3,
      biomes: ['Meadows'],
      maxSpawned: 1,
      interval: 120,
      chance: 0.506,
      killed: 'gd_king',
      groupRadius: 1,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 30 }), stagger: 1.34, name: 'bite' },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 5,
      swim: 1,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 150,
    stagger: {
      factor: 0.5,
      time: 1.34,
    },
    damageModifiers: grayModifiers,
    drop: [
      dropEntry('GreydwarfEye', { chance: 0.5, min: 2, max: 2 }),
      dropEntry('Stone', { min: 2, max: 2 }),
      dropEntry('Wood', { min: 3, max: 5 }),
      dropEntry('Dandelion'),
      dropEntry('AncientSeed', { chance: 0.33 }),
      dropTrophy('TrophyGreydwarfBrute', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Bjorn',
    ragdollId: 'Bjorn_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal'],
    tier: 2,
    upgradeDistance: 2000,
    emoji: '🐻',
    faction: 'ForestMonsters',
    factionGroup: 'bjorn',
    spawners: [spawner({
      tier: 2,
      biomes: ['BlackForest'],
      maxSpawned: 1,
      interval: 700,
      chance: 0.3,
      night: false,
      levels: [1, 2],
    }), spawner({
      tier: 2,
      biomes: ['BlackForest'],
      maxSpawned: 1,
      interval: 700,
      chance: 0.4,
      night: true,
      levels: [1, 3],
    })],
    attacks: single([
      { dmg: dmg({ pierce: 55 }), force: 40, name: 'bite' },
      { dmg: dmg({ slash: 50, chop: 40 }), force: 40, name: 'claws' },
      { dmg: dmg({ slash: 25, chop: 40 }), force: 35, name: 'swipe_l' },
      { dmg: dmg({ slash: 25, chop: 40 }), force: 35, name: 'swipe_r' },
      { dmg: dmg({ slash: 25, chop: 40 }), force: 40, name: 'swipe_combo' },
      { dmg: dmg({ blunt: 50, chop: 40, pickaxe: 40 }), force: 100, name: 'slam' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 3,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 80,
      run: 100,
      swim: 50,
    },
    hp: 800,
    regenAllHPTime: 3000,
    stagger: {
      factor: 0.3,
      time: 2.74,
    },
    damageModifiers: {
      blunt: 'resistant',
      slash: 'normal',
      pierce: 'resistant',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'weak',
      frost: 'resistant',
      lightning: 'normal',
      poison: 'normal',
      spirit: 'immune',
    },
    drop: [
      dropEntry('BjornPaw'),
      dropEntry('BjornMeat', { min: 2, max: 3 }),
      dropEntry('BjornHide', { min: 4, max: 5 }),
      dropTrophy('TrophyBjorn', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Troll',
    ragdollId: 'Troll_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 2,
    upgradeDistance: 2000,
    emoji: '',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 2,
      biomes: ['BlackForest'],
      maxSpawned: 1,
      interval: 4000,
      chance: 0.05,
      minDistance: 2000,
    })],
    attacks: [
      {
        rate: 2,
        variety: 'unarmed',
        attacks: [
          // troll_punch
          { dmg: dmg({ blunt: 60, chop: 100, pickaxe: 40 }), name: '1-hand hit', stagger: 4.4, force: 100, toolTier: 2 },
          // troll_groundslam
          { dmg: dmg({ blunt: 70, chop: 100, pickaxe: 40 }), name: '2-hand smash', stagger: 1.98, force: 100, toolTier: 2 },
          // troll_throw
          { dmg: dmg({ blunt: 50, chop: 60, pickaxe: 40 }), name: 'throw', stagger: 2.16, toolTier: 0 },
        ],
      }, {
        rate: 1,
        variety: 'log',
        attacks: [
          // troll_log_swing_v
          { dmg: dmg({ blunt: 70, chop: 100, pickaxe: 40 }), name: 'v-swing', stagger: 2.74, force: 80, toolTier: 2 },
          // troll_log_swing_h
          { dmg: dmg({ blunt: 60, chop: 100, pickaxe: 40 }), name: 'h-swing', stagger: 2.74, force: 80, toolTier: 2 },
        ],
      },
    ],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 3,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 100,
      run: 250,
      swim: 100,
    },
    hp: 600,
    stagger: {
      factor: 0.3,
      time: 2.74,
    },
    damageModifiers: {
      ...animalDmgModifiers,
      blunt: 'resistant',
      pierce: 'weak',
    },
    weakSpots: [
      {
        location: 'head',
        damageModifiers: {
          ...animalDmgModifiers,
          pierce: 'veryWeak',
        }
      }
    ],
    drop: [
      dropEntry('TrollHide', { min: 5, max: 5 }),
      dropEntry('Coins', { min: 20, max: 30 }),
      dropTrophy('TrophyFrostTroll', 0.5),
    ],
  },
  {
    type: 'creature',
    id: 'gd_king', // the elder
    ragdollId: 'gdking_Ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 2,
    emoji: '🥦',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      // SCREAM
      { spawn: ['TentaRoot'], number: [15, 15], max: 30 },
      { dmg: dmg({ pierce: 35, chop: 20, pickaxe: 20 }), name: 'Vine Shoot', burst: 25, toolTier: 0 },
      { dmg: dmg({ blunt: 60, chop: 1000, pickaxe: 1000 }), name: 'Stomp', force: 30, toolTier: 0, collider: areaCollider(5) },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 3,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 100,
      run: 200,
      swim: 100,
    },
    hp: 2500,
    stagger: null,
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'veryWeak',
      poison: 'immune',
      spirit: 'immune',
    },
    drop: [
      dropEntry('CryptKey', { perPlayer: true }),
      dropTrophy('TrophyTheElder', 1),
    ],
  },
  {
    type: 'creature',
    id: 'TentaRoot',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 2,
    emoji: '🥦',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      { dmg: dmg({ blunt: 55, chop: 20, pickaxe: 20 }), name: 'poke', force: 40, toolTier: 0 }
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 0,
      run: 0,
      swim: 0,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 20,
    stagger: null,
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'weak',
      frost: 'resistant',
      poison: 'immune',
    },
    drop: [],
    timedDestruction: [18, 20],
  },
  // {
  //   id: 'piece_TrainingDummy',
  //   type: 'creature',
  //   tier: 2,
  //   emoji: '🥊',
  //   // piece: { target: 'random', water: false, size: [2, 0.3, 2] },
  //   // recipe: { type: 'craft_piece', materials: { FineWood: 5, BronzeNails: 10, Ectoplasm: 5 }, station: 'piece_workbench' },
  // },
// SWAMP
  {
    type: 'creature',
    group: 'blob',
    id: 'Blob',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 3,
    emoji: '🦠',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      maxSpawned: 2,
      interval: 500,
      chance: 0.2,
      distance: 20,
      groupSize: [1, 2],
      groupRadius: 1,
      altitude: [-1, 1000],
      levels: [1, 1],
    })],
    attacks: single([{ dmg: dmg({ poison: 90 }), name: 'poison', unblockable, collider: areaCollider(4) }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 2,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 100,
      swim: 100,
    },
    hp: 50,
    stagger: null,
    damageModifiers: blobDamageModifiers,
    drop: [
      dropEntry('Ooze', { min: 1, max: 2 }),
      dropTrophy('TrophyBlob', 0.1),
    ],
  },
  {
    type: 'creature',
    group: 'blob',
    id: 'BlobElite',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 3,
    emoji: '🦠',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      maxSpawned: 1,
      interval: 120,
      chance: 0.2,
      distance: 20,
      groupRadius: 1,
      night: true,
      altitude: [-1, 1000],
      levels: [1, 1],
    })],
    attacks: single([{ dmg: dmg({ poison: 115 }), name: 'poison', unblockable, collider: areaCollider(8) }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 2,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 100,
      swim: 100,
    },
    hp: 150,
    stagger: null,
    damageModifiers: blobDamageModifiers,
    drop: [
      dropEntry('Ooze', { min: 2, max: 3 }),
      dropEntry('IronScrap', { chance: 0.33 }),
      dropTrophy('TrophyBlob', 0.1),
      dropEntry('Blob', { min: 2, max: 2 }),
    ],
  },
  {
    type: 'creature',
    id: 'Leech',
    ragdollId: '',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 3,
    emoji: '🧛',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      maxSpawned: 10,
      interval: 200,
      chance: 0.5,
      distance: 5,
      groupRadius: 2,
      altitude: [-3, -0.1],
    })],
    attacks: single([{ dmg: dmg({
      pierce: 20,
      poison: 70,
    }), name: 'bite', force: 30 }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 2,
      swim: 3,
    },
    turnSpeed: {
      walk: 50,
      run: 50,
      swim: 150,
    },
    hp: 60,
    stagger: null,
    damageModifiers: {
      ...animalDmgModifiers,
      fire: 'immune',
      poison: 'resistant',
      spirit: 'immune',
    },
    drop: [
      dropEntry('Bloodbag'),
      dropTrophy('TrophyLeech', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Surtling',
    // warmthRadius: 3,
    PointLight: { color: '#FFB174', range: 3, intensity: 3 },
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 3,
    emoji: '🧨',
    faction: 'Demon',
    spawners: [],
    attacks: single([{
      dmg: dmg({ blunt: 10, fire: 40 }),
      name: 'fireball', stagger: 1.14, force: 30,
    }]),
    tolerate: TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 6,
      swim: 2,
    },
    turnSpeed: {
      walk: 400,
      run: 400,
      swim: 400,
    },
    hp: 20,
    stagger: {
      factor: 0.5,
      time: 1.14,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'immune',
      frost: 'weak',
      poison: 'immune',
      spirit: 'immune',
    },
    drop: [
      dropEntry('Coal', { min: 4, max: 5 }),
      dropEntry('SurtlingCore', { chance: 0.5 }),
      dropTrophy('TrophySurtling', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Wraith',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['fly'],
    tier: 3,
    emoji: '👻',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      maxSpawned: 2,
      interval: 200,
      chance: 0.3,
      distance: 20,
      groupRadius: 1,
      night: true,
      altitude: [-2, 1000],
      offset: 50,
      levels: [1, 1],
    })],
    attacks: single([{ dmg: dmg({ slash: 60, }), name: 'slash', stagger: 2.04, force: 60 }]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2.5,
      run: 5,
      swim: 1,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 100,
    stagger: {
      factor: 0.5,
      time: 2.04,
    },
    damageModifiers: {
      blunt: 'resistant',
      slash: 'resistant',
      pierce: 'resistant',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'weak',
      frost: 'immune',
      lightning: 'normal',
      poison: 'immune',
      spirit: 'weak',
    },
    drop: [
      dropEntry('Chain'),
      dropTrophy('TrophyWraith', 0.05),
    ],
  },
  ...(function(x: Creature) { return [x, {
    ...x,
    id: 'Draugr_Ranged',
    iconId: 'creature/Draugr',
    ragdollId: 'Draugr_ranged_ragdoll',
    attacks: [x.attacks[1]!],
  }] }({
    type: 'creature',
    id: 'Draugr',
    ragdollId: 'Draugr_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 3,
    emoji: '🧟',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      maxSpawned: 4,
      interval: 120,
      chance: 0.5,
      groupSize: [1, 2],
      altitude: [-1.5, 10],
    }), spawner({
      tier: 3,
      biomes: ['Meadows', 'BlackForest', 'Mountain', 'Plains'],
      maxSpawned: 3,
      interval: 300,
      chance: 0.05,
      killed: 'gd_king',
      envs: ['Misty'],
      groupSize: [1, 3],
      night: true,
      levels: [1, 1],
    })],
    attacks: [
      { variety: 'axe',
        rate: 3,
        attacks: [{ dmg: dmg({ slash: 48, chop: 15 }), name: 'axe', stagger: 2.8, force: 60, toolTier: 0 }],
      },
      { variety: 'bow',
        rate: 1,
        attacks: [{ dmg: dmg({ pierce: 48 }), name: 'bow', stagger: 2.8, force: 18 }],
      },
    ],
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 5,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 300,
    },
    hp: 100,
    stagger: {
      factor: 0.5,
      time: 2.8,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'resistant',
      poison: 'immune',
    },
    drop: [
      dropEntry('Entrails'),
      dropTrophy('TrophyDraugr', 0.1),
    ],
  })),
  {
    type: 'creature',
    id: 'Draugr_Elite',
    ragdollId: 'Draugr_elite_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 3,
    emoji: '🧟',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 300,
      chance: 0.05,
      distance: 15,
      night: true,
      altitude: [-1.5, 10],
      levels: [1, 1],
    })],
    attacks: single([ { dmg: dmg({ slash: 58, }), name: 'sword', stagger: 2.8, force: 60 } ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 6,
      swim: 1,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 300,
    },
    hp: 200,
    stagger: {
      factor: 0.5,
      time: 2.8,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'resistant',
      poison: 'immune',
    },
    drop: [
      dropEntry('Entrails', { min: 2, max: 3 }),
      dropTrophy('TrophyDraugrElite', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Abomination',
    ragdollId: 'Abomination_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 3,
    emoji: '🥦',
    faction: 'Undead',
    spawners: [spawner({
      tier: 3,
      biomes: ['Swamp'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 4000,
      chance: 0.35,
      distance: 30,
      altitude: [-2, 5],
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ blunt: 60, chop: 100, pickaxe: 60 }), name: 'swing', stagger: 2, force: 130, toolTier: 2, collider: areaCollider(4.3) },
      { dmg: dmg({ blunt: 80, chop: 100, pickaxe: 60 }), name: 'slam', stagger: 2, force: 130, toolTier: 2, collider: areaCollider(5.4) },
      { dmg: dmg({ blunt: 80, chop: 100, pickaxe: 60 }), name: 'stub', stagger: 2, force: 130, toolTier: 2 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 5,
      swim: 3,
    },
    turnSpeed: {
      walk: 20,
      run: 60,
      swim: 45,
    },
    hp: 800,
    stagger: {
      factor: 0.33,
      time: 2,
    },
    damageModifiers: {
      blunt: 'resistant',
      slash: 'normal',
      pierce: 'veryResistant',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'weak',
      frost: 'immune',
      lightning: 'normal',
      poison: 'immune',
      spirit: 'immune',
    },
    drop: [
      dropEntry('Root', { min: 5, max: 5 }),
      dropEntry('Guck', { min: 3, max: 5 }),
      dropTrophy('TrophyAbomination', 0.5),
    ],
  },
  {
    type: 'creature',
    id: 'BogWitchKvastur',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'MonsterAI'],
    tier: 3,
    emoji: '🧹',
    faction: 'Dverger',
    aggravatable,
    spawners: [],
    attacks: single([
      { dmg: dmg({ blunt: 80 }), name: 'attack', force: 30, toolTier: 0 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 100,
    },
    hp: 700,
    stagger: {
      factor: 0.3,
      time: 2,
    },
    damageModifiers: {
      blunt: 'normal',
      slash: 'normal',
      pierce: 'normal',
      chop: 'weak',
      pickaxe: 'ignore',
      fire: 'veryWeak',
      frost: 'normal',
      lightning: 'normal',
      poison: 'immune',
      spirit: 'immune',
    },
    drop: [
      dropEntry('Wood'),
      dropEntry('Resin'),
      dropTrophy('TrophyKvastur', 1),
    ],
  },
  {
    type: 'creature',
    id: 'Bonemass',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 3,
    emoji: '🦠',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      { dmg: dmg({ poison: 130 }), name: 'poison', force: 0, unblockable, undodgeable },
      { dmg: dmg({
        blunt: 80,
        chop: 1000,
        pickaxe: 1000,
        poison: 50,
      }), name: 'punch', force: 100, toolTier: 0 },
      { spawn: ['Skeleton', 'Blob'], number: [4, 4], max: 8, }
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 4,
      swim: 2,
    },
    turnSpeed: {
      walk: 50,
      run: 100,
      swim: 50,
    },
    hp: 5000,
    stagger: null,
    damageModifiers: {
      ...defaultDmgModifiers,
      blunt: 'weak',
      slash: 'resistant',
      pierce: 'veryResistant',
      fire: 'veryResistant',
      frost: 'weak',
      poison: 'immune',
    },
    drop: [
      dropEntry('Wishbone', { perPlayer: true }),
      dropEntry('TrophyBonemass', { scale: false }),
    ],
  },
// MOUNTAINS
  {
    type: 'creature',
    group: 'blob',
    id: 'BlobFrost',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 4,
    emoji: '🦠',
    faction: 'PlayerSpawned',
    spawners: [],
    attacks: single([{ dmg: dmg({ frost: 100 }), name: 'nova', unblockable, collider: areaCollider(4) }]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 2,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 100,
      swim: 100,
    },
    hp: 50,
    stagger: null,
    damageModifiers: { ...blobDamageModifiers, frost: 'veryResistant' },
    drop: [
      dropEntry('Crystal', { min: 1, max: 2 }),
    ],
  },
  {
    type: 'creature',
    id: 'Bat',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal', 'fly'],
    tier: 4,
    emoji: '',
    faction: 'MountainMonsters',
    spawners: [],
    attacks: single([
      { dmg: dmg({ slash: 20 }), name: 'bite', stagger: 1.68, force: 30 },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 5,
      run: 10,
      swim: 0,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 200,
    },
    hp: 10,
    damageModifiers: mods([1, 1, 1, 4, 4, 2, 3, 0, 3, 2]),
    stagger: {
      factor: 0.5,
      time: 1.68,
    },
    drop: [
      dropEntry('LeatherScraps', { chance: 0.5 }),
    ],
  },
  {
    type: 'creature',
    id: 'Ulv',
    ragdollId: 'Ulv_Ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    spawners: [],
    attacks: single([
      { dmg: dmg({ slash: 80 }), name: 'bite', stagger: 1.4, force: 30 },
      { dmg: dmg({ slash: 70 }), name: 'slash', stagger: 1.4, force: 130 },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 8,
      swim: 2,
    },
    turnSpeed: {
      walk: 150,
      run: 200,
      swim: 100,
    },
    hp: 50,
    damageModifiers: { ...defaultDmgModifiers, fire: 'veryResistant', poison: 'weak' },
    stagger: {
      factor: 0.5,
      time: 1.4,
    },
    drop: [
      dropEntry('WolfFang', { chance: 0.5, min: 1, max: 2 }),
      dropTrophy('TrophyUlv', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Wolf',
    ragdollId: 'Wolf_Ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Procreation', 'Tameable'],
    tags: ['animal'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    factionGroup: 'wolf',
    spawners: [spawner({
      tier: 4,
      biomes: ['Mountain'],
      biomeAreas: 6,
      maxSpawned: 3,
      interval: 400,
      chance: 0.256,
      distance: 30,
      groupSize: [1, 2],
      groupRadius: 5,
      night: false,
      tilt: [0, 45],
      levels: [1, 1],
    }), spawner({
      tier: 4,
      biomes: ['Mountain'],
      biomeAreas: 6,
      maxSpawned: 6,
      interval: 120,
      chance: 0.5,
      distance: 30,
      groupSize: [2, 4],
      groupRadius: 5,
      night: true,
      tilt: [0, 45],
    })],
    attacks: single([
      { dmg: dmg({ slash: 70 }), name: 'bite', stagger: 4.8, force: 30 }, // 3 different animations, same stats
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.5,
      run: 8,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 200,
      swim: 100,
    },
    hp: 80,
    damageModifiers: animalDmgModifiers,
    stagger: {
      factor: 0.5,
      time: 4.8,
    },
    drop: [
      dropEntry('WolfFang', { chance: 0.4 }),
      dropEntry('WolfMeat', { chance: 1 }),
      dropEntry('WolfPelt', { max: 2 }),
      dropTrophy('TrophyWolf', 0.1),
    ],
    tame: { tameTime: 1800, fedTime: 600, commandable: true,
            eats: ['RawMeat', 'DeerMeat', 'NeckTail', 'LoxMeat', 'Sausages', 'FishRaw', 'ChickenMeat'] },
            // eatRange:1.4, searchRange:10, heal:20
    pregnancy: { points: 3, time: 60, chance: 0.33, grow: 3000, childId: 'Wolf_cub' }, // max: 4, range: 3
  },
  {
    type: 'creature',
    id: 'Wolf_cub',
    iconId: 'creature/Wolf',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Growup'],
    tags: ['animal'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    factionGroup: 'wolf',
    spawners: [],
    attacks: [],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.5,
      run: 4,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 200,
      swim: 100,
    },
    hp: 10,
    damageModifiers: animalDmgModifiers,
    stagger: { factor: 0.5, time: NaN, },
    drop: [],
  },
  {
    type: 'creature',
    id: 'Fenring',
    ragdollId: 'Fenring_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    spawners: [spawner({
      tier: 4,
      biomes: ['Mountain'],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 400,
      chance: 0.2,
      groupRadius: 1,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 85 }), name: 'hit', stagger: 1.12, force: 60 },
      { dmg: dmg({ slash: 95 }), name: 'jump', stagger: 1.32, force: 100 },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.5,
      run: 7,
      swim: 2,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 300,
    stagger: {
      factor: 0.5,
      time: 1.12,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'weak',
      poison: 'resistant',
    },
    drop: [
      dropEntry('WolfFang'),
      dropTrophy('TrophyFenring', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Fenring_Cultist',
    ragdollId: 'Fenring_cultist_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    spawners: [spawner({
      tier: 4,
      biomes: [],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 400,
      chance: 0.2,
      groupRadius: 1,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 35, fire: 40 }), name: 'claw', stagger: 1.32, force: 60 },
      { dmg: dmg({ slash: 35, fire: 40 }), name: 'claw2', stagger: 1.12, force: 60 },
      // fenring_attack_flames
      { dmg: dmg({ fire: 50 }), name: 'flames', stagger: 1.12, unblockable, force: 0 },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 5,
      run: 5,
      swim: 2,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 200,
    stagger: {
      factor: 0.5,
      time: 1.12,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'immune',
      poison: 'weak',
    },
    drop: [
      dropEntry('JuteRed', { min: 1, max: 3 }),
      dropTrophy('TrophyCultist', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Fenring_Cultist_Hildir',
    iconId: 'resource/TrophyCultist_Hildir',
    group: 'semiboss',
    ragdollId: 'Fenring_cultist_ragdoll_hildir',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    spawners: [],
    attacks: single([
      // Fenring_attack_iceclaw_double
      { dmg: dmg({ slash: 60, frost: 70 }), name: 'claw', stagger: 1.32, force: 60 },
      // Fenring_attack_iceclaw
      { dmg: dmg({ slash: 40, frost: 70 }), name: 'claw2', stagger: 1.12, force: 60 },
      // Fenring_attack_frost
      { dmg: dmg({ frost: 100 }), name: 'frost', stagger: 1.12, force: 0, unblockable },
      // Fenring_attack_IceNova
      { dmg: dmg({ frost: 90 }), name: 'nova', stagger: 1.12, force: 0, unblockable, undodgeable },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.25,
      run: 5,
      swim: 2,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 100,
    },
    hp: 3700,
    stagger: {
      factor: 0.5,
      time: NaN,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'weak',
      frost: 'immune',
    },
    drop: [
      dropTrophy('chest_hildir2', 1),
      dropTrophy('TrophyCultist_Hildir', 1),
    ],
  },
  {
    type: 'creature',
    id: 'Fenring_Cultist_Hildir_nochest',
    iconId: 'resource/TrophyCultist_Hildir',
    ragdollId: 'Fenring_cultist_ragdoll_hildir',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 4,
    emoji: '🐺',
    faction: 'MountainMonsters',
    spawners: [],
    attacks: single([
      // Fenring_attack_iceclaw_double
      { dmg: dmg({ slash: 60, frost: 70 }), name: 'claw', stagger: 1.32, force: 60 },
      // Fenring_attack_iceclaw
      { dmg: dmg({ slash: 40, frost: 70 }), name: 'claw2', stagger: 1.12, force: 60 },
      // Fenring_attack_frost
      { dmg: dmg({ frost: 100 }), name: 'frost', stagger: 1.12, force: 0, unblockable },
      // Fenring_attack_IceNova
      { dmg: dmg({ frost: 20 }), name: 'nova', stagger: 1.12, force: 50 },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1.25,
      run: 5,
      swim: 2,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 100,
    },
    hp: 1850,
    stagger: {
      factor: 0.5,
      time: NaN,
    },
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'weak',
      frost: 'immune',
    },
    drop: [
      dropTrophy('TrophyCultist_Hildir', 1),
    ],
  },
  {
    type: 'creature',
    id: 'StoneGolem',
    ragdollId: 'Stonegolem_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 4,
    emoji: '🗿',
    faction: 'ForestMonsters',
    spawners: [spawner({
      tier: 4,
      biomes: ['Mountain'],
      biomeAreas: 6,
      maxSpawned: 1,
      interval: 120,
      chance: 0.256,
      groupRadius: 4,
      night: true,
      altitude: [120, 1000],
      levels: [1, 1],
    })],
    attacks: [
      { rate: 1,
        variety: 'spike',
        attacks: [
          { dmg: dmg({
            blunt: 110,
            chop: 100,
            pickaxe: 100,
          }), name: 'spike', stagger: 2.94, force: 130, toolTier: 0 },
          { dmg: dmg({
            blunt: 110,
            chop: 100,
            pickaxe: 100,
          }), name: 'spike sweep', stagger: 0.98, force: 130, toolTier: 0 },
          // spikes, no dmg, no animation
        ],
      },
      { rate: 1,
        variety: 'sledge',
        attacks: [
          { dmg: dmg({
            blunt: 110,
            chop: 100,
            pickaxe: 100,
          }), name: 'slam', stagger: 2.94, force: 130, toolTier: 0 }, // R8
          { dmg: dmg({
            blunt: 110,
            chop: 100,
            pickaxe: 100,
          }), name: 'double smash', stagger: 0.98, force: 120, toolTier: 0 }, // R8.66
        ],
      },
    ], 
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: {
      walk: 2,
      run: 4,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 100,
      run: 150,
      swim: 100,
    },
    hp: 800,
    stagger: {
      factor: 0.33,
      time: 0.98,
    },
    damageModifiers: {
      blunt: 'normal',
      slash: 'resistant',
      pierce: 'resistant',
      chop: 'ignore',
      pickaxe: 'veryWeak',
      fire: 'immune',
      frost: 'immune',
      lightning: 'normal',
      poison: 'immune',
      spirit: 'immune',
    },
    drop: [
      dropEntry('Stone', { min: 5, max: 10 }),
      dropEntry('Crystal', { min: 8, max: 12 }),
      dropTrophy('TrophySGolem', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Hatchling', // drake
    ragdollId: 'Hatchling_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal', 'fly'],
    tier: 4,
    emoji: '🐉',
    faction: 'MountainMonsters',
    spawners: [spawner({
      tier: 4,
      biomes: ['Mountain'],
      biomeAreas: 6,
      maxSpawned: 2,
      interval: 120,
      chance: 0.256,
      groupRadius: 4,
      night: true,
      altitude: [100, 1000],
      tilt: [0, 66],
      offset: 10,
      levels: [1, 1],
    })],
    attacks: single([{ dmg: dmg({ frost: 90 }), burst: 3, name: 'ice shards', force: 30, }]), // burst interval: 0.3
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 4,
      run: 12,
      swim: 1,
    },
    turnSpeed: {
      walk: 100,
      run: 100,
      swim: 100,
    },
    hp: 100,
    stagger: null,
    damageModifiers: {
      ...animalDmgModifiers,
      fire: 'weak',
      frost: 'immune',
    },
    drop: [
      dropEntry('FreezeGland', { max: 2 }),
      dropTrophy('TrophyHatchling', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'Dragon',
    ragdollId: '',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['fly'],
    tier: 4,
    emoji: '🐲',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      // taunt,
      { dmg: dmg({
        pierce: 120,
        chop: 1000,
        pickaxe: 1000
      }), name: 'bite', force: 120, toolTier: 3 }, // R8
      { dmg: dmg({
        slash: 110,
        chop: 1000,
        pickaxe: 1000
      }), name: 'claw', force: 120, toolTier: 3 }, // R4 two: left & right
      { dmg: dmg({
        pierce: 30,
        chop: 200,
        pickaxe: 200,
        frost: 200
      }), burst: 16, name: 'ice shards', force: 30, toolTier: 3 }, // velocity=2/25 burstInterval=0.05, spread=20/13
      { dmg: dmg({
        chop: 200,
        pickaxe: 200,
        frost: 200,
      }), name: 'breath', force: 40, toolTier: 3 },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 4, // 10
      run: 6, // 20
      swim: 4,
    },
    turnSpeed: {
      walk: 60, // 80
      run: 60, // 80
      swim: 60,
    },
    hp: 7500,
    stagger: null,
    damageModifiers: {
      ...animalDmgModifiers,
      fire: 'weak',
      frost: 'immune',
    },
    drop: [
      dropEntry('DragonTear', { min: 10, max: 10, scale: false }),
      dropEntry('TrophyDragonQueen', { scale: false }),
    ],
  },
// PLAINS
  {
    type: 'creature',
    group: 'goblin',
    id: 'Goblin', // Fulling
    ragdollId: 'Goblin_Dragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    spawners: [spawner({
      tier: 5,
      biomes: ['Plains'],
      maxSpawned: 2,
      interval: 1000,
      chance: 0.33,
      distance: 30,
      groupSize: [1, 2],
      night: false,
    }), spawner({
      tier: 5,
      biomes: ['Plains'],
      maxSpawned: 5,
      interval: 60,
      chance: 0.33,
      distance: 30,
      groupSize: [3, 5],
      night: true,
    }), spawner({
      tier: 6,
      biomes: ['Meadows', 'BlackForest', 'Mountain'],
      maxSpawned: 3,
      interval: 3000,
      chance: 0.05,
      killed: 'GoblinKing',
      groupSize: [3, 3],
      night: true,
      levels: [1, 1],
    })],
    // weapon: 2 club, 1 spear, 2 sword, 1 torch
    // shield: 1 wood, 2 <null>
    attacks: [
      { rate: 2, variety: 'club', attacks: [{ dmg: dmg({ blunt: 85 }), name: 'club', stagger: 2.08 }] },
      { rate: 1, variety: 'spear', attacks: [{ dmg: dmg({ pierce: 85 }), name: 'spear', stagger: 3.38 }] },
      { rate: 2, variety: 'sword', attacks: [{ dmg: dmg({ slash: 85 }), name: 'sword', stagger: 2.08 }] },
      { rate: 1, variety: 'torch', attacks: [{ dmg: dmg({ blunt: 45, fire: 45 }), name: 'torch', stagger: 2.08 }] },
    ],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 175,
    stagger: {
      factor: 0.3,
      time: 2.08,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Coins', { chance: 0.25, min: 5, max: 10 }),
      dropEntry('BlackMetalScrap', { min: 1, max: 2 }),
      dropTrophy('TrophyGoblin', 0.1),
    ],
    // alertRange: 20,
    // maxChase: 300,
  },
  {
    type: 'creature',
    group: 'goblin',
    id: 'GoblinShaman',
    ragdollId: 'GoblinShaman_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    spawners: [spawner({
      tier: 5,
      biomes: ['Meadows'],
      maxSpawned: 1,
      interval: 120,
      chance: 0.506,
      killed: 'gd_king',
      groupRadius: 1,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ blunt: 100 }), name: 'staff', stagger: 3.2 },
      { dmg: dmg({ blunt: 20, fire: 100 }), name: 'fireball', stagger: 3.2 },
      { cast: 'GoblinShaman_shield' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1,
      run: 2,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 100,
    stagger: {
      factor: 0.3,
      time: 3.2,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Coins', { chance: 0.25, min: 20, max: 40 }),
      dropEntry('BlackMetalScrap', { min: 1, max: 2 }),
      dropEntry('Pukeberries', { min: 1, max: 2 }),
      dropTrophy('TrophyGoblinShaman', 0.1),
    ],
  },
  {
    type: 'creature',
    group: 'semiboss',
    id: 'GoblinShaman_Hildir',
    iconId: 'resource/TrophyGoblinBruteBrosShaman',
    ragdollId: 'GoblinShaman_Hildir_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    spawners: [],
    attacks: single([
      { dmg: dmg({ blunt: 100 }), name: 'staff', stagger: 3.2 },
      { dmg: dmg({ blunt: 20, fire: 100 }), name: 'fireball', stagger: 3.2 },
      { cast: 'GoblinShaman_shield' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1,
      run: 2,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 2400,
    stagger: {
      factor: 0.3,
      time: 3.2,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropTrophy('chest_hildir3', 1),
      dropTrophy('TrophyGoblinBruteBrosShaman', 1),
    ],
  },
  {
    type: 'creature',
    id: 'GoblinShaman_Hildir_nochest',
    iconId: 'resource/TrophyGoblinBruteBrosShaman',
    ragdollId: 'GoblinShaman_Hildir_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    spawners: [],
    attacks: single([
      { dmg: dmg({ blunt: 100 }), name: 'staff', stagger: 3.2 },
      { dmg: dmg({ blunt: 20, fire: 100 }), name: 'fireball', stagger: 3.2 },
      { cast: 'GoblinShaman_shield' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1,
      run: 2,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 200,
      run: 200,
      swim: 200,
    },
    hp: 1200,
    stagger: {
      factor: 0.3,
      time: 3.2,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropTrophy('TrophyGoblinBruteBrosShaman', 1),
    ],
  },
  {
    type: 'creature',
    group: 'goblin',
    id: 'GoblinBrute', // Berserk
    ragdollId: 'GoblinBrute_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    maxLvl: 3,
    spawners: [],
    attacks: single([
      { dmg: dmg({
        blunt: 130,
        chop: 100,
        pickaxe: 40,
      }), name: 'attack', stagger: 2.88, force: 50, toolTier: 2 }, // random: 2
      { dmg: dmg({
        blunt: 130,
        chop: 100,
        pickaxe: 40,
      }), name: 'rageattack', stagger: 2.88, force: 70, toolTier: 2 },
      // taunt
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 5,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 150,
      run: 300,
      swim: 150,
    },
    hp: 800,
    stagger: {
      factor: 0.3,
      time: 2.88,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Coins', { min: 5, max: 20 }),
      dropEntry('BlackMetalScrap', { min: 3, max: 5 }),
      dropTrophy('GoblinTotem', 0.1),
      dropTrophy('TrophyGoblinBrute', 0.05),
    ],
  },
  {
    type: 'creature',
    group: 'semiboss',
    id: 'GoblinBrute_Hildir',
    iconId: 'resource/TrophyGoblinBruteBrosBrute',
    disabled: true,
    ragdollId: 'GoblinBrute_Hildir_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '💪',
    faction: 'PlainsMonsters',
    maxLvl: 1,
    spawners: [],
    attacks: single([
      { dmg: dmg({
        blunt: 150,
        chop: 100,
        pickaxe: 40,
      }), name: 'attack', stagger: 2.88, force: 100, toolTier: 2 },
      { dmg: dmg({
        blunt: 130,
        chop: 100,
        pickaxe: 40,
      }), name: 'rageattack', stagger: 2.88, force: 100, toolTier: 2 },
      // taunt
      // HipCloth
      // ShoulderGuard
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 5,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 150,
      run: 300,
      swim: 100,
    },
    hp: 800,
    stagger: {
      factor: 0.3,
      time: NaN,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('chest_hildir3'),
    ],
  },
  {
    type: 'creature',
    group: 'semiboss',
    id: 'GoblinBruteBros',
    iconId: 'resource/TrophyGoblinBruteBrosBrute',
    ragdollId: 'GoblinBrute_Hildir_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    maxLvl: 1,
    spawners: [],
    attacks: single([
      { dmg: dmg({
        blunt: 150,
        chop: 100,
        pickaxe: 40,
      }), name: 'attack', stagger: 2.88, force: 100, toolTier: 2 },
      { dmg: dmg({
        blunt: 130,
        chop: 100,
        pickaxe: 40,
      }), name: 'rageattack', stagger: 2.88, force: 100, toolTier: 2 },
      // taunt
      // HipCloth
      // ShoulderGuard
      { dmg: dmg({ // GoblinShaman_attack_fireball_hildir
        blunt: 20,
        fire: 100,
      }), name: 'rageattack', stagger: 2.88, force: 80 },
      // GoblinShaman_attack_protect_hildir
      { cast: 'GoblinShaman_shield' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 5,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 150,
      run: 300,
      swim: 100,
    },
    hp: 4200,
    stagger: {
      factor: 0.3,
      time: NaN,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('GoblinShaman_Hildir'),
      dropTrophy('TrophyGoblinBruteBrosBrute', 1),
    ],
  },
  {
    type: 'creature',
    id: 'GoblinBruteBros_nochest',
    iconId: 'resource/TrophyGoblinBruteBrosBrute',
    ragdollId: 'GoblinBrute_Hildir_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'VisEquipment'],
    tier: 5,
    emoji: '',
    faction: 'PlainsMonsters',
    maxLvl: 1,
    spawners: [],
    attacks: single([
      { dmg: dmg({
        blunt: 150,
        chop: 100,
        pickaxe: 40,
      }), name: 'attack', stagger: 2.88, force: 100, toolTier: 2 },
      { dmg: dmg({
        blunt: 130,
        chop: 100,
        pickaxe: 40,
      }), name: 'rageattack', stagger: 2.88, force: 100, toolTier: 2 },
      // taunt
      // HipCloth
      // ShoulderGuard
      { dmg: dmg({ // GoblinShaman_attack_fireball_hildir
        blunt: 20,
        fire: 100,
      }), name: 'rageattack', stagger: 2.88, force: 80 },
      // GoblinShaman_attack_protect_hildir
      { cast: 'GoblinShaman_shield' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 5,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 150,
      run: 300,
      swim: 100,
    },
    hp: 2100,
    stagger: {
      factor: 0.3,
      time: NaN,
    },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('GoblinShaman_Hildir_nochest'),
      dropTrophy('TrophyGoblinBruteBrosBrute', 1),
    ],
  },
  {
    type: 'creature',
    id: 'Deathsquito',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['fly'],
    tier: 5,
    emoji: '🦟',
    faction: 'PlainsMonsters',
    spawners: [spawner({
      tier: 5,
      biomes: ['Plains'],
      maxSpawned: 3,
      interval: 500,
      chance: 0.2,
      distance: 30,
      groupSize: [1, 2],
      groupRadius: 10,
      altitude: [1, 1000],
      tilt: [0, 99],
      levels: [1, 1],
    })],
    attacks: single([{ dmg: dmg({ pierce: 90 }), name: 'bite' }]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 5,
      run: 12,
      swim: 1,
    },
    turnSpeed: {
      walk: 500,
      run: 500,
      swim: 500,
    },
    hp: 10,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Needle', { scale: false }),
      dropTrophy('TrophyDeathsquito', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Lox',
    ragdollId: 'lox_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Procreation', 'Saddle', 'Tameable'],
    tags: ['animal'],
    tier: 5,
    emoji: '🐂',
    faction: 'PlainsMonsters',
    factionGroup: 'lox',
    spawners: [spawner({
      tier: 5,
      biomes: ['Plains'],
      maxSpawned: 3,
      interval: 1000,
      chance: 0.05,
      distance: 100,
      groupSize: [2, 4],
      groupRadius: 10,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 130 }), name: 'bite', force: 150 },
      { dmg: dmg({ blunt: 120, chop: 100, pickaxe: 100, }), name: 'stomp', force: 100, toolTier: 0, collider: areaCollider(4.5) },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 6,
      swim: 3,
    },
    turnSpeed: {
      walk: 70,
      run: 100,
      swim: 50,
    },
    hp: 1000,
    stagger: {
      factor: 0.3,
      time: 3.16,
    },
    damageModifiers: loxDamageModifiers,
    drop: [
      dropEntry('LoxMeat', { min: 4, max: 6 }),
      dropEntry('LoxPelt', { min: 2, max: 3 }),
      dropTrophy('TrophyLox', 0.1),
    ],
    tame: { tameTime: 1800, fedTime: 600, commandable: false,
            eats: ['Cloudberry', 'Barley', 'Flax'] },
            // eatRange:4, searchRange:10, heal:10
    pregnancy: { points: 4, time: 120, chance: 0.33, grow: 6000, childId: 'Lox_Calf' },
  },
  {
    type: 'creature',
    id: 'Lox_Calf',
    iconId: 'creature/Lox',
    ragdollId: 'loxcalf_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Growup'],
    tags: ['animal'],
    tier: 5,
    emoji: '🐂',
    faction: 'PlainsMonsters',
    factionGroup: 'lox',
    spawners: [],
    attacks: [],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 6,
      swim: 3,
    },
    turnSpeed: {
      walk: 120,
      run: 300,
      swim: 50,
    },
    hp: 1000,
    stagger: {
      factor: 0.3,
      time: NaN,
    },
    damageModifiers: loxDamageModifiers,
    drop: [dropEntry('LoxMeat', { scale: false })],
  },
  {
    type: 'creature',
    id: 'Unbjorn',
    ragdollId: 'Unbjorn_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 5,
    upgradeDistance: 2000,
    emoji: '🐻',
    faction: 'PlainsMonsters',
    factionGroup: 'bjorn',
    spawners: [spawner({
      tier: 5,
      biomes: ['Plains'],
      maxSpawned: 1,
      interval: 4000,
      chance: 0.25,
      night: true,
      minDistance: 2000,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 20, pierce: 130 }), force: 40, name: 'bite' },
      { dmg: dmg({ slash: 130, chop: 40 }), force: 40, name: 'claws' },
      { dmg: dmg({ slash: 60, chop: 40 }), force: 35, name: 'swipe_l' },
      { dmg: dmg({ slash: 60, chop: 40 }), force: 35, name: 'swipe_r' },
      { dmg: dmg({ slash: 60, chop: 40 }), force: 40, name: 'swipe_combo' },
      { dmg: dmg({ blunt: 150, chop: 40, pickaxe: 40 }), force: 100, name: 'slam' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 3,
      run: 6,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 80,
      run: 100,
      swim: 50,
    },
    hp: 1200,
    regenAllHPTime: 3000,
    stagger: {
      factor: 0.3,
      time: 2.74,
    },
    damageModifiers: {
      blunt: 'resistant',
      slash: 'normal',
      pierce: 'resistant',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'weak',
      frost: 'resistant',
      lightning: 'normal',
      poison: 'normal',
      spirit: 'immune',
    },
    drop: [
      dropTrophy('TrophyBjornUndead', 0.1),
      dropEntry('BjornMeat', { min: 2, max: 3 }),
      dropEntry('RottenMeat', { min: 1, max: 2, chance: 0.8 }),
      dropEntry('UndeadBjornRibcage', { min: 1, max: 3 }),
      dropEntry('BjornHide', { min: 1, max: 2 }),
    ],
  },
  {
    type: 'creature',
    group: 'blob',
    id: 'BlobTar',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 5,
    emoji: '🦠',
    faction: 'Undead',
    spawners: [],
    attacks: single([
      { dmg: dmg({ blunt: 45, poison: 50, }), name: 'stomp', force: 80, toolTier: 0 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE | TOLERATE.TAR,
    speed: {
      walk: 1,
      run: 3,
      swim: 4,
    },
    turnSpeed: {
      walk: 100,
      run: 100,
      swim: 100,
    },
    hp: 100,
    stagger: null,
    damageModifiers: {
      blunt: 'weak',
      slash: 'resistant',
      pierce: 'resistant',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'weak',
      frost: 'resistant',
      lightning: 'normal',
      poison: 'immune',
      spirit: 'normal',
    },
    drop: [
      dropEntry('Tar'),
      dropTrophy('TrophyGrowth', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'GoblinKing',
    ragdollId: 'GoblinKing_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 5,
    emoji: '🦴',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      { dmg: dmg({ // 10 meteors
        blunt: 40,
        chop: 50,
        pickaxe: 50,
        fire: 120,
      }), burst: 20, name: 'meteors', force: 100, unblockable }, // no toolTier
      { dmg: dmg({
        chop: 50,
        pickaxe: 50,
        fire: 40,
        lightning: 20,
      }), name: 'fire Breath' }, // aka beam burst: 20*0.1 V=30/40 spread=1/1
      { dmg: dmg({
        chop: 100,
        pickaxe: 100,
        fire: 65,
        lightning: 65,
      }), name: 'nova', force: 100, toolTier: 2, unblockable, collider: areaCollider(8) },
      // Taunt
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 4,
      swim: 1.5,
    },
    turnSpeed: {
      walk: 50,
      run: 50,
      swim: 50,
    },
    hp: 10000,
    stagger: null,
    damageModifiers: {
      ...defaultDmgModifiers,
      pierce: 'veryResistant',
      fire: 'resistant',
      poison: 'immune',
    },
    drop: [
      dropEntry('YagluthDrop', { min: 3, max: 3, scale: false }),
      dropTrophy('TrophyGoblinKing', 1),
    ],
  },
  {
    type: 'creature',
    id: 'Hen',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Procreation', 'Tameable'],
    tags: ['animal', 'bird'],
    tier: 6,
    emoji: '🐔',
    faction: 'ForestMonsters',
    factionGroup: 'chicken',
    spawners: [],
    attacks: single([{ dmg: dmg({ blunt: 10 }), stagger: 1.24, name: 'beak' }]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 3,
      run: 6,
      swim: 2,
    },
    turnSpeed: {
      walk: 100,
      run: 200,
      swim: 100,
    },
    hp: 10,
    damageModifiers: animalDmgModifiers,
    stagger: { factor: 0.5, time: NaN },
    drop: [
      dropEntry('ChickenMeat'),
      dropEntry('Feathers', { max: 3 }),
    ],
    tame: { tameTime: 1800, fedTime: 600, commandable: false,
            eats: ['Dandelion', 'Barley', 'BeechSeeds', 'BirchSeeds', 'CarrotSeeds', 'OnionSeeds', 'TurnipSeeds'] },
            // eatRange:1, searchRange:10, heal:20
    pregnancy: { points: 3, time: 60, chance: 0.33, grow: 60, childId: 'ChickenEgg' }, // max: 10, range: 4
  },
  {
    type: 'creature',
    id: 'Chicken',
    iconId: 'creature/Hen',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Growup'],
    tags: ['animal'],
    tier: 6,
    emoji: '🐤',
    faction: 'ForestMonsters',
    factionGroup: 'chicken',
    spawners: [],
    attacks: [],
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 4,
      run: 6,
      swim: 2,
    },
    turnSpeed: {
      walk: 150,
      run: 150,
      swim: 100,
    },
    hp: 10,
    damageModifiers: animalDmgModifiers,
    stagger: { factor: 0.5, time: NaN, },
    drop: [
      dropEntry('ChickenMeat', { chance: 0.25 }),
      dropEntry('Feathers', { chance: 0.5, max: 2 }),
    ],
  },
  {
    type: 'creature',
    id: 'Hare',
    iconId: 'resource/TrophyHare',
    ragdollId: 'Hare_ragdoll',
    components: ['Character'],
    tags: ['animal'],
    tier: 6,
    emoji: '🐇',
    faction: 'AnimalsVeg',
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 3,
      maxSpawned: 10,
      interval: 100,
      chance: 0.5,
      distance: 10,
      groupSize: [1, 3],
      groupRadius: 6,
      levels: [1, 3],
    })],
    attacks: [],

    tolerate: TOLERATE.WATER,
    speed: {
      walk: 4,
      run: 8,
      swim: 2,
    },
    turnSpeed: {
      walk: 200,
      run: 300,
      swim: 100,
    },
    hp: 10,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('HareMeat'),
      dropEntry('ScaleHide', { max: 3 }),
      dropTrophy('TrophyHare', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Skeleton_Friendly',
    iconId: 'resource/TrophySkeleton',
    ragdollId: null,
    components: ['Character'],
    tags: ['animal'],
    tier: 6,
    emoji: '💀',
    faction: 'PlayerSpawned',
    spawners: [],
    attacks: [
      {
        variety: 'melee',
        rate: 4,
        // skeleton_sword2
        attacks: [{ dmg: dmg({ slash: 40 }), name: 'sword', force: 40 }],
      },
      {
        variety: 'ranged',
        rate: 1,
        // skeleton_bow2
        attacks: [{ dmg: dmg({ pierce: 40 }), name: 'bow', force: 15 }],
      },
    ],
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 4,
      swim: 0,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 0,
    },
    hp: 400,
    stagger: null,
    damageModifiers: skeletonDamageModifiers,
    drop: [],
  },
  {
    type: 'creature',
    id: 'Dverger',
    iconId: 'resource/TrophyDvergr',
    ragdollId: 'Dverger_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 6,
    emoji: '🏹',
    faction: 'Dverger',
    aggravatable,
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 1000,
      chance: 0.3,
      distance: 30,
      groupSize: [1, 2],
      groupRadius: 3,
      night: false,
      levels: [1, 3],
    }), spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 1000,
      chance: 0.1,
      distance: 30,
      groupSize: [1, 2],
      groupRadius: 3,
      night: false,
      levels: [1, 3],
    })],
    attacks: single([
      { dmg: dmg({ pierce: 110 }), name: 'arbalest', force: 200, stagger: 1.68 },
      { dmg: dmg({ blunt: 70 }), name: 'melee', force: 80, stagger: 1.84 },
      // visual: DvergerArbalest
      // visual: DvergerSuitArbalest
    ]),

    tolerate: TOLERATE.WATER,
    speed: { walk: 2, run: 7, swim: 1.5 },
    turnSpeed: { walk: 200, run: 200, swim: 100 },
    hp: 350,
    stagger: { factor: 0.3, time: 1.68 },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Softtissue', { chance: 0.25, min: 1, max: 2 }),
      dropEntry('BlackMarble', { chance: 0.5, min: 1, max: 2 }),
      dropEntry('Coins', { min: 2, max: 15 }),
      dropTrophy('TrophyDvergr', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'DvergerMage',
    iconId: 'resource/TrophyDvergr',
    ragdollId: 'Dverger_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 6,
    emoji: '🧙‍♂️',
    faction: 'Dverger',
    aggravatable,
    spawners: [],
    // visual: DvergerHairMale
    // visual: DvergerHairFemale
    attacks: [
      {
        rate: 1,
        variety: 'Fire',
        attacks: [
          { dmg: dmg({ blunt: 70 }), name: 'melee', force: 80, stagger: 1.82 },
          { dmg: dmg({ fire: 100 }), name: 'fireball', force: 50, stagger: 1.68 }, // radius=6
          // visual: DvergerStaffFire
          // visual: DvergerSuitFire
          { dmg: dmg({ fire: 100 }), name: 'clusterbomb', force: 50, stagger: 1.68, burst: 3 }, // radius=3
        ],
      },
      {
        rate: 1,
        variety: 'Ice',
        attacks: [
          { dmg: dmg({ blunt: 70 }), name: 'melee', force: 80, stagger: 1.82 },
          { dmg: dmg({ frost: 200 }), name: 'icebolt', force: 50, stagger: 1.68 },
          // visual: DvergerStaffIce
          { dmg: dmg({ frost: 150 }), name: 'nova', force: 0, stagger: 1.68 }, // radius=5
          // visual: DvergerSuitIce
        ],
      },
      {
        rate: 1,
        variety: 'Healer',
        attacks: [
          // stagger: 1.68
          { cast: 'SE_Dvergr_buff' }, // radius=8
          { cast: 'SE_Dvergr_heal' }, // radius=4.28
          // visual: DvergerStaffHeal
          // visual: DvergerSuitSupport
          { spawn: ['Mistile'], name: 'mistile', number: [1, 3], max: 9 },
        ],
      },
    ],

    tolerate: TOLERATE.WATER,
    speed: { walk: 2, run: 7, swim: 1.5 },
    turnSpeed: { walk: 200, run: 200, swim: 100 },
    hp: 350,
    stagger: { factor: 0.3, time: 1.68 },
    damageModifiers: { ...animalDmgModifiers, fire: 'resistant', frost: 'resistant' },
    drop: [
      dropEntry('Softtissue', { chance: 0.25, max: 2, scale: false }),
      dropEntry('BlackMarble', { chance: 0.5, max: 2, scale: false }),
      dropEntry('Coins', { min: 2, max: 15, scale: false }),
      dropTrophy('TrophyDvergr', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Mistile',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    PointLight: { color: '#EE433E', range: 10, intensity: 1.5 },
    tier: 2,
    emoji: '🔵',
    faction: 'Dverger',
    aggravatable,
    spawners: [],
    attacks: single([
      { dmg: dmg({ blunt: 150 }), name: 'kamikaze', force: 20, toolTier: 0, collider: areaCollider(1.5) }
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: {
      walk: 5,
      run: 5,
      swim: 0,
    },
    turnSpeed: {
      walk: 500,
      run: 500,
      swim: 0,
    },
    hp: 1,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [],
    timedDestruction: [20, 25],
  },
  {
    type: 'creature',
    id: 'Tick',
    iconId: 'resource/TrophyTick',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'MonsterAI'],
    tier: 6,
    emoji: '✔',
    faction: 'MistlandsMonsters',
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 4,
      interval: 3000,
      chance: 0.05,
      distance: 10,
      groupSize: [3, 3],
      groupRadius: 3,
      night: true,
      levels: [1, 3],
    })],
    attacks: single([
      { dmg: dmg({ pierce: 50 }), name: 'stick', stagger: 1.66 },
    ]),

    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1,
      run: 8,
      swim: 0,
    },
    turnSpeed: {
      walk: 300,
      run: 400,
      swim: 0,
    },
    hp: 50,
    stagger: {
      factor: 0.5,
      time: 1.54,
    },
    damageModifiers: {
      ...animalDmgModifiers,
      pierce: 'resistant',
    },
    drop: [
      dropEntry('GiantBloodSack'),
      dropTrophy('TrophyTick', 0.05),
    ],
  },
  {
    type: 'creature',
    id: 'Gjall',
    iconId: 'resource/TrophyGjall',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    PointLight: { color: '#FF4517', range: 8, intensity: 1.5 },
    tier: 6,
    emoji: '🎈',
    faction: 'MistlandsMonsters',
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 4000,
      chance: 0.1,
      distance: 50,
      groupRadius: 1,
      night: false,
      tilt: [0, 90],
      offset: 6,
      levels: [1, 1],
    }), spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 4000,
      chance: 0.1,
      distance: 50,
      groupRadius: 1,
      night: true,
      tilt: [0, 90],
      offset: 6,
      levels: [1, 3],
    })],
    attacks: single([
      { dmg: dmg({ blunt: 50, fire: 80 }), name: 'spit', force: 30, burst: 2 }, // interval = 0.7
      { spawn: ['Tick'], number: [1, 3], max: 8, name: 'eggs' },
      { dmg: dmg({ blunt: 20 }), name: 'shake', force: 150, unblockable, undodgeable, collider: areaCollider(4.81) },
    ]),

    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 6,
      swim: 4,
    },
    turnSpeed: {
      walk: 150,
      run: 250,
      swim: 60,
    },
    hp: 1500,
    stagger: null,
    damageModifiers: {
      blunt: 'normal',
      slash: 'normal',
      pierce: 'normal',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'resistant',
      frost: 'normal',
      lightning: 'normal',
      poison: 'normal',
      spirit: 'immune',
    },
    weakSpots: [{
      location: 'belly',
      damageModifiers: {
        blunt: 'normal',
        slash: 'normal',
        pierce: 'veryWeak',
        chop: 'ignore',
        pickaxe: 'ignore',
        fire: 'normal',
        frost: 'normal',
        lightning: 'normal',
        poison: 'normal',
        spirit: 'immune',
      }
    }],
    drop: [
      dropEntry('Bilebag', { scale: false }),
      dropTrophy('TrophyGjall', 0.3),
    ],
  },
  {
    type: 'creature',
    id: 'SeekerBrood',
    ragdollId: null,
    PointLight: { color: '#FF9547', range: 4, intensity: 1 },
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['insect'],
    tier: 6,
    emoji: '🐜',
    faction: 'MistlandsMonsters',
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 5,
      interval: 3000,
      chance: 0.05,
      distance: 10,
      groupSize: [3, 3],
      groupRadius: 3,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ pierce: 60 }), name: 'pincers', force: 40, stagger: 1.86 },
    ]),

    tolerate: TOLERATE.WATER,
    speed: {
      walk: 1,
      run: 3,
      swim: 4,
    },
    turnSpeed: {
      walk: 150,
      run: 250,
      swim: 60,
    },
    hp: 20,
    stagger: {
      factor: 0.3,
      time: 1.86,
    },
    damageModifiers: seekerDamageModifiers,
    drop: [dropEntry('RoyalJelly', { max: 3 })],
  },
  {
    type: 'creature',
    id: 'Seeker',
    iconId: 'resource/TrophySeeker',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['insect'],
    tier: 6,
    emoji: '🐜',
    faction: 'MistlandsMonsters',
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 200,
      chance: 0.3,
      distance: 30,
      groupSize: [2, 3],
      groupRadius: 6,
      night: false,
      levels: [1, 2],
    }), spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 6,
      interval: 300,
      chance: 0.2,
      distance: 40,
      groupSize: [2, 3],
      groupRadius: 6,
      night: true,
      levels: [1, 3],
    }), spawner({
      tier: 6,
      biomes: ['Meadows', 'BlackForest', 'Swamp', 'Mountain', 'Plains'],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 3000,
      chance: 0.05,
      distance: 10,
      killed: 'SeekerQueen',
      groupSize: [3, 3],
      groupRadius: 3,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ pierce: 90 }), name: 'pincers', force: 40, stagger: 2.95 },
      { dmg: dmg({ pierce: 120 }), name: 'claw', force: 20, stagger: 2.95 },
      { dmg: dmg({ pierce: 120 }), name: 'claw2', force: 20, stagger: 2.95 },
      { dmg: dmg({ blunt: 100 }), name: 'slam', force: 150, stagger: 2.95 }, // + flying
    ]),

    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 6,
      swim: 4,
    },
    turnSpeed: {
      walk: 150,
      run: 250,
      swim: 60,
    },
    hp: 200,
    stagger: {
      factor: 0.3,
      time: 1.84,
    },
    damageModifiers: seekerDamageModifiers,
    drop: [
      dropEntry('BugMeat', { max: 2 }),
      dropEntry('Carapace', { max: 2 }),
      dropTrophy('TrophySeeker', 0.1),
    ],
  },
  {
    type: 'creature',
    id: 'SeekerBrute',
    iconId: 'resource/TrophySeekerBrute',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['insect'],
    tier: 6,
    emoji: '🐜',
    faction: 'MistlandsMonsters',
    spawners: [spawner({
      tier: 6,
      biomes: ['Mistlands'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 4000,
      chance: 0.1,
      distance: 40,
      groupRadius: 12,
      levels: [1, 3],
    })],
    attacks: single([
      { dmg: dmg({ blunt: 100, chop: 100, pickaxe: 100 }), name: 'ram', force: 200, toolTier: 4, stagger: 2.5 },
      { dmg: dmg({ slash: 100 }), name: 'bite', force: 70, stagger: 2.48 },
      { dmg: dmg({ blunt: 120, chop: 100, pickaxe: 100 }), name: 'slam', force: 100, stagger: 2.36, collider: areaCollider(4) },
    ]),

    tolerate: TOLERATE.WATER,
    speed: {
      walk: 2,
      run: 6,
      swim: 4,
    },
    turnSpeed: {
      walk: 150,
      run: 250,
      swim: 60,
    },
    hp: 1500,
    stagger: {
      factor: 0.3,
      time: 2.36,
    },
    damageModifiers: seekerDamageModifiers,
    weakSpots: [{
      location: 'ass',
      damageModifiers: {
        blunt: 'weak',
        slash: 'weak',
        pierce: 'weak',
        chop: 'ignore',
        pickaxe: 'ignore',
        fire: 'weak',
        frost: 'weak',
        lightning: 'weak',
        poison: 'normal',
        spirit: 'immune',
      },
    }],
    drop: [
      dropEntry('BugMeat', { max: 2 }),
      dropEntry('Carapace', { min: 2, max: 4 }),
      dropTrophy('TrophySeekerBrute', 0.05),
      dropEntry('Mandible', { min: 1, max: 2, scale: false }),
    ],
  },
  {
    type: 'creature',
    id: 'SeekerQueen',
    iconId: 'resource/TrophySeekerQueen',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['insect'],
    tier: 6,
    emoji: '🐜👑',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      // SeekerQueen_Teleport: aiMaxHp: 0.9
      // SeekerQueen_Rush
      { dmg: dmg({ slash: 100, chop: 300, pickaxe: 300 }), name: 'rush', force: 250, toolTier: 3, aiMaxHp: 0.6 },
      // SeekerQueen_Bite
      { dmg: dmg({ pierce: 140, chop: 300, pickaxe: 300, poison: 100 }), name: 'bite', force: 250, toolTier: 3, aiMaxHp: 0.7 },
      // SeekerQueen_Call
      { spawn: ['Seeker'], number: [1, 3], max: 6, name: 'call', aiMaxHp: 0.99 },
      // SeekerQueen_Spit does a burst of 20x, aiHp: [0, 0.9]
      // SeekerQueen_projectile_spit, each has 30% spawn on hit
      // SeekerQueen_SpitSpawnAbility, which spawn 1 SeekerBrood
      { spawn: ['SeekerBrood'], number: [4, 8], max: 30, name: 'spit' }, // 20 with p=0.3
      { dmg: dmg({ slash: 130, chop: 300, pickaxe: 300 }), name: 'slap', force: 250, toolTier: 3 },
      { dmg: dmg({ pierce: 150, chop: 300, pickaxe: 300 }), name: 'pirce_aoe', force: 250, toolTier: 3, collider: areaCollider(4.5) },
    ]),
    tolerate: TOLERATE.WATER,
    speed: { walk: 4, run: 8, swim: 4 },
    turnSpeed: { walk: 50, run: 120, swim: 60, },
    hp: 12500,
    stagger: null,
    damageModifiers: {
      blunt: 'normal',
      slash: 'normal',
      pierce: 'resistant',
      chop: 'ignore',
      pickaxe: 'ignore',
      fire: 'normal',
      frost: 'normal',
      lightning: 'normal',
      poison: 'normal',
      spirit: 'immune',
    },
    drop: [
      dropTrophy('TrophySeekerQueen', 1),
      dropEntry('QueenDrop', { min: 3, scale: false }),
    ],
  },
// OCEAN
  {
    type: 'creature',
    id: 'Serpent',
    ragdollId: '',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal'],
    tier: 2,
    emoji: '🐍',
    faction: 'SeaMonsters',
    spawners: [spawner({
      tier: 2,
      biomes: ['Ocean'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 1000,
      chance: 0.05,
      distance: 50,
      night: true,
      altitude: [-1000, -5],
      levels: [1, 1],
    }), spawner({
      tier: 2,
      biomes: ['Ocean'],
      biomeAreas: 2,
      maxSpawned: 1,
      interval: 1000,
      chance: 0.05,
      distance: 50,
      envs: ['ThunderStorm', 'Rain'],
      altitude: [-1000, -5],
      levels: [1, 1],
    })],
    attacks: single([
      { dmg: dmg({ slash: 70, }),
        name: 'attack', force: 100,
      },
      // taunt
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 4,
      run: 4,
      swim: 10,
    },
    turnSpeed: {
      walk: 20,
      run: 20,
      swim: 100,
    },
    hp: 400,
    stagger: null,
    damageModifiers: {
      ...animalDmgModifiers,
      fire: 'immune',
      frost: 'weak',
      poison: 'resistant',
    },
    drop: [
      dropEntry('SerpentScale', { min: 8, max: 10 }),
      dropEntry('SerpentMeat', { min: 6, max: 8 }),
      dropTrophy('TrophySerpent', 0.33),
    ],
  },
  {
    type: 'creature',
    id: 'BonemawSerpent',
    iconId: 'resource/TrophyBonemawSerpent',
    ragdollId: '',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal'],
    tier: 7,
    emoji: '🐍',
    faction: 'SeaMonsters',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 7,
      maxSpawned: 1,
      interval: 1000,
      chance: 0.2,
      distance: 50,
      altitude: [-1000, -5],
      levels: [1, 1],
    })],
    attacks: single([
      // BonemawSerpent_spit
      { dmg: dmg({ blunt: 40, fire: 20, poison: 20 }), stagger: NaN, force: 30, name: 'spit' },
      // BonemawSerpent_taunt
      // BonemawSerpent_bite
      { dmg: dmg({ slash: 140 }), stagger: NaN, force: 100, name: 'bite' },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 4,
      run: 4,
      swim: 12,
    },
    turnSpeed: {
      walk: 20,
      run: 20,
      swim: 100,
    },
    hp: 1100,
    stagger: null,
    damageModifiers: {
      ...animalDmgModifiers,
      fire: 'immune',
      frost: 'weak',
      poison: 'resistant',
    },
    drop: [
      dropTrophy('TrophyBonemawSerpent', 0.33),
      dropEntry('BoneMawSerpentMeat', { min: 6, max: 8 }),
      dropEntry('BonemawSerpentTooth', { min: 8, max: 10 }),
    ],
  },
  {
    type: 'creature',
    id: 'Volture',
    iconId: 'resource/TrophyVolture',
    ragdollId: '',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['animal'],
    tier: 7,
    emoji: '🦃',
    faction: 'Demon',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 3,
      maxSpawned: 3,
      interval: 120,
      chance: 0.2,
      distance: 10,
      groupSize: [1, 2],
      groupRadius: 3,
      altitude: [-20, -2],
      offset: 20,
      levels: [1, 1],
    })],
    attacks: single([
      // volture_talons
      { dmg: dmg({ slash: 110 }), stagger: 0.9, force: 5, name: 'talons' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: { walk: 7, run: 13, swim: 0 },
    turnSpeed: { walk: 300, run: 300, swim: 0 },
    hp: 200,
    stagger: { factor: 0.5, time: 0.9 },
    damageModifiers: {
      ...defaultDmgModifiers,
      frost: 'weak',
      poison: 'immune',
    },
    drop: [
      dropTrophy('TrophyVolture', 0.1),
      dropEntry('VoltureMeat'),
      dropEntry('Feathers', { min: 2, max: 3, chance: 0.5 }),
      dropEntry('VoltureEgg', { min: 1, max: 2, chance: 0.5 }),
    ],
  },
  {
    type: 'creature',
    id: 'Asksvin',
    iconId: 'resource/TrophyAsksvin',
    ragdollId: 'Ragdoll_Asksvin',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Procreation', 'Tameable'],
    tags: ['animal'],
    tier: 1,
    emoji: '🐗',
    faction: 'Demon',
    factionGroup: 'asksvin',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 2,
      maxSpawned: 3,
      interval: 220,
      chance: 0.3,
      distance: 25,
      radius: [50, 0],
      groupSize: [2, 4],
      groupRadius: 10,
      night: false,
      altitude: [9, 1000],
      tilt: [0, 45],
      levels: [1, 2],
    }), spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 2,
      maxSpawned: 4,
      interval: 220,
      chance: 0.45,
      distance: 20,
      groupSize: [2, 4],
      groupRadius: 10,
      night: true,
      altitude: [9, 1000],
      tilt: [0, 45],
      levels: [1, 3],
    })],
    attacks: single([
      // Asksvin_Bite
      { dmg: dmg({ blunt: 75, slash: 75, chop: 50, pickaxe: 50 }), stagger: 1.24, force: 50, name: 'bite' },
      // Asksvin_Headbutt
      { dmg: dmg({ blunt: 120, chop: 100, pickaxe: 100 }), toolTier: 4, stagger: 1.24, force: 200, name: 'ram' },
      // Asksvin_Pounce
      { dmg: dmg({ blunt: 150, chop: 50, pickaxe: 100 }), stagger: 1.24, force: 100, name: 'pounce' },
    ]),
    tolerate: TOLERATE.WATER,
    speed: {
      walk: 3,
      run: 7,
      swim: 3,
    },
    turnSpeed: {
      walk: 95,
      run: 120,
      swim: 50,
    },
    hp: 800,
    stagger: {
      factor: 0.3,
      time: 1.24,
    },
    damageModifiers: { ...animalDmgModifiers, fire: 'resistant', poison: 'resistant' },
    drop: [
      dropTrophy('TrophyAsksvin', 0.1),
      dropEntry('AskBladder'),
      dropEntry('AskHide', { min: 2, max: 3 }),
      dropEntry('AsksvinMeat', { min: 2, max: 3 }),
    ],
    tame: { tameTime: 1800, fedTime: 600, commandable: false,
            eats: ['Vineberry', 'Fiddleheadfern', 'MushroomSmokePuff'] },
            // eatRange:1.0, searchRange:10, heal:5
    pregnancy: { points: 3, time: 60, chance: 0.33, grow: 3000, childId: 'AsksvinEgg' },
  },
  {
    type: 'creature',
    id: 'Asksvin_hatchling',
    iconId: 'resource/TrophyAsksvin',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI', 'Growup'],
    tags: ['animal'],
    tier: 7,
    emoji: '🐗',
    faction: 'Demon',
    factionGroup: 'asksvin',
    spawners: [],
    attacks: [],
    tolerate: TOLERATE.WATER,
    speed: { walk: 3, run: 9, swim: 0 },
    turnSpeed: { walk: 95, run: 120, swim: 0 },
    hp: 400,
    stagger: { factor: 0.3, time: NaN },
    damageModifiers: { ...animalDmgModifiers, fire: 'resistant', poison: 'resistant' },
    drop: [
      dropEntry('AskBladder', { chance: 0.2 }),
      dropEntry('AskHide', { chance: 0.2 }),
      dropEntry('AsksvinMeat', { chance: 0.2 }),
    ],
  },
  {
    type: 'creature',
    id: 'Troll_Summoned',
    iconId: 'resource/TrophyTroll',
    ragdollId: 'Troll_summoned_ragdoll',
    components: ['Character'],
    tags: ['animal'],
    tier: 7,
    emoji: '💀',
    faction: 'PlayerSpawned',
    spawners: [],
    attacks: [
      {
        rate: 1,
        variety: 'unarmed',
        attacks: [
          // troll_summoned_punch
          { dmg: dmg({ blunt: 60, pickaxe: 40 }), name: '1-hand hit', stagger: 4.4, force: 100, toolTier: 2 },
          // troll_summoned_groundslam
          { dmg: dmg({ blunt: 70, chop: 100, pickaxe: 40 }), name: '2-hand smash', stagger: 1.98, force: 100, toolTier: 2 },
          // troll_summoned_throw
          { dmg: dmg({ blunt: 80, chop: 60, pickaxe: 40, fire: 20 }), name: 'throw', stagger: 2.16, force: 60, toolTier: 0 },
        ],
      }, {
        rate: 1,
        variety: 'log',
        attacks: [
          // troll_summoned_log_swing_v
          { dmg: dmg({ blunt: 70, chop: 100, pickaxe: 40 }), name: 'v-swing', stagger: 2.74, force: 80, toolTier: 2 },
          // troll_summoned_log_swing_h
          { dmg: dmg({ blunt: 60, chop: 100, pickaxe: 40 }), name: 'h-swing', stagger: 2.74, force: 80, toolTier: 2 },
        ],
      },
    ],
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: {
      walk: 1,
      run: 4,
      swim: 0,
    },
    turnSpeed: {
      walk: 300,
      run: 300,
      swim: 0,
    },
    hp: 2000,
    stagger: null,
    damageModifiers: mods([1, 0, 0, 4, 4, 3, 2, 0, 0, 3]),
    drop: [],
  },
  {
    type: 'creature',
    id: 'DvergerAshlands',
    iconId: 'resource/TrophyDvergr',
    ragdollId: 'Dverger_ragdoll',
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 7,
    emoji: '🏹',
    faction: 'Dverger',
    aggravatable,
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 2,
      maxSpawned: 2,
      interval: 1000,
      chance: 0.1,
      distance: 30,
      groupSize: [1, 2],
      groupRadius: 3,
      radius: [50, 0],
      night: false,
      tilt: [0, 35],
      levels: [1, 3],
    })],
    attacks: single([
      // visual: DvergerArbalest
      // DvergerArbalest_shootAshlands
      { dmg: dmg({ pierce: 210 }), name: 'arbalest', force: 30, stagger: 1.68 },
      // Dverger_meleeAshlands
      { dmg: dmg({ blunt: 70 }), name: 'melee', force: 80, stagger: 1.84 },
      // visual: DvergerSuitArbalest_Ashlands
    ]),

    tolerate: TOLERATE.WATER,
    speed: { walk: 2, run: 7, swim: 1.5 },
    turnSpeed: { walk: 200, run: 200, swim: 100 },
    hp: 1000,
    stagger: { factor: 0.3, time: 1.68 },
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('Softtissue', { chance: 0.25, min: 1, max: 2 }),
      dropEntry('BlackMarble', { chance: 0.5, min: 1, max: 2 }),
      dropEntry('Coins', { min: 2, max: 15 }),
      dropTrophy('TrophyDvergr', 0.05),
    ],
  },
  ...variations({
    type: 'creature',
    id: 'Charred_Archer',
    iconId: 'resource/TrophyCharredArcher',
    ragdollId: null,
    components: ['Character'],
    tags: ['skeleton'],
    tier: 7,
    emoji: '💀',
    faction: 'Demon',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 7,
      maxSpawned: 4,
      interval: 320,
      chance: 0.2,
      distance: 10,
      groupSize: [1, 2],
      groupRadius: 15,
      altitude: [1, 1000],
      levels: [1, 2],
    }), spawner({
      tier: 7,
      biomes: ['Meadows', 'BlackForest', 'Swamp', 'Mountain', 'Plains'],
      biomeAreas: 7,
      maxSpawned: 3,
      interval: 3000,
      chance: 0.05,
      distance: 10,
      killed: 'Fader',
      groupSize: [1, 3],
      groupRadius: 3,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      // charred_bow
      { dmg: dmg({ pierce: 120 }), name: 'bow', force: 15, burst: 10 },
      // charred_bow_volley
      { dmg: dmg({ pierce: 60 }), name: 'volley', force: 15 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: { walk: 1.5, run: 4, swim: 0 },
    turnSpeed: { walk: 400, run: 300, swim: 0 },
    hp: 400,
    stagger: { factor: 0.5, time: 1.5 },
    damageModifiers: charredDmgModifiers,
    drop: [
      dropEntry('CharredBone', { max: 3 }),
      dropTrophy('TrophyCharredArcher', 0.05),
    ],
  }, {
    id: 'Charred_Archer_Fader',
    damageModifiers: charredSummonDmgModifiers,
    spawners: [],
    attacks: single([
      // charred_bow_Fader
      { dmg: dmg({ pierce: 60 }), name: 'bow', force: 15 },
      // charred_bow_volley_Fader
      { dmg: dmg({ pierce: 30 }), name: 'volley', force: 15 },
    ]),
    hp: 50,
    drop: [],
  }),
  {
    type: 'creature',
    id: 'Charred_Mage',
    iconId: 'resource/TrophyCharredMage',
    ragdollId: null,
    components: ['Character'],
    tags: ['skeleton'],
    tier: 7,
    emoji: '💀',
    faction: 'Demon',
    spawners: [],
    attacks: single([
      // Charred_HipCloth
      // charred_magestaff_fire
      // Charred_MageCloths
      // charred_magestaff_summon
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: { walk: 1.5, run: 4, swim: 0 },
    turnSpeed: { walk: 400, run: 300, swim: 0 },
    hp: 600,
    stagger: { factor: 0.5, time: 1.5 },
    damageModifiers: charredDmgModifiers,
    drop: [
      dropEntry('CharredBone', { max: 3 }),
      dropTrophy('TrophyCharredMage', 0.05),
    ],
  },
  ...variations({
    type: 'creature',
    id: 'Charred_Melee',
    iconId: 'resource/TrophyCharredMelee',
    ragdollId: 'Charred_Melee_Ragdoll',
    components: ['Character'],
    tags: ['skeleton'],
    tier: 7,
    emoji: '💀',
    faction: 'Demon',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 2,
      maxSpawned: 4,
      interval: 320,
      chance: 0.2,
      distance: 10,
      groupSize: [1, 2],
      groupRadius: 15,
      altitude: [-1000, 1000],
      levels: [1, 3],
    }), spawner({
      tier: 7,
      biomes: ['Meadows', 'BlackForest', 'Swamp', 'Mountain', 'Plains'],
      biomeAreas: 7,
      maxSpawned: 3,
      interval: 3000,
      chance: 0.05,
      distance: 10,
      killed: 'Fader',
      groupSize: [1, 3],
      groupRadius: 3,
      night: true,
      levels: [1, 1],
    })],
    attacks: single([
      // Charred_HipCloth
      // charred_greatsword_swing
      { dmg: dmg({ slash: 150 }), name: 'swing', force: 40 },
      // charred_greatsword_thrust
      { dmg: dmg({ pierce: 160 }), name: 'thrust', force: 40 },
      // charred_greatsword_feint
      { dmg: dmg({ slash: 130 }), name: 'feint', force: 40 },
      // charred_greatsword_thrustfeint
      { dmg: dmg({ pierce: 160 }), name: 'thrust-feint', force: 40 },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: { walk: 1.5, run: 4, swim: 0 },
    turnSpeed: { walk: 400, run: 300, swim: 0 },
    hp: 600,
    stagger: { factor: 0.5, time: 1.5 },
    damageModifiers: charredDmgModifiers,
    drop: [
      dropEntry('CharredBone', { max: 3 }),
      dropTrophy('TrophyCharredMelee', 0.05),
    ],
  }, {
    id: 'Charred_Melee_Dyrnwyn',
    minLvl: 3,
    maxLvl: 3,
    spawners: [],
    attacks: single([
      // Charred_HipCloth
      // charred_dyrnwyn_greatsword_swing
      { dmg: dmg({ slash: 240 }), name: 'swing', force: 40 },
      // charred_dyrnwyn_greatsword_thrust
      { dmg: dmg({ pierce: 230 }), name: 'thrust', force: 40 },
      // charred_dyrnwyn_greatsword_feint
      { dmg: dmg({ slash: 250 }), name: 'feint', force: 40 },
      // charred_dyrnwyn_greatsword_thrustfeint
      { dmg: dmg({ pierce: 210 }), name: 'thrust-feint', force: 40 },
      // Charred_Helmet
      // Charred_Breastplate
    ]),
    hp: 2500,
    damageModifiers: mods([0, 0, 1, 4, 4, 1, 1, 0, 3, 2]),
    drop: [
      dropEntry('DyrnwynHiltFragment', { scale: false }),
    ],
  }, {
    id: 'Charred_Melee_Fader',
    spawners: [],
    attacks: single([
      // Charred_HipCloth
      // charred_fader_greatsword_swing
      { dmg: dmg({ slash: 75 }), name: 'swing', force: 40 },
      // charred_fader_greatsword_thrust
      { dmg: dmg({ pierce: 80 }), name: 'thrust', force: 40 },
      // charred_fader_greatsword_feint
      { dmg: dmg({ slash: 65 }), name: 'feint', force: 40 },
      // charred_fader_greatsword_thrustfeint
      { dmg: dmg({ pierce: 80 }), name: 'thrust-feint', force: 40 },
    ]),
    damageModifiers: charredSummonDmgModifiers,
    hp: 50,
    drop: [],
  }),
  ...variations({
    type: 'creature',
    id: 'Charred_Twitcher',
    iconId: 'resource/TrophyCharredMelee',
    ragdollId: null,
    components: ['Character'],
    tags: ['skeleton'],
    tier: 7,
    emoji: '💀',
    faction: 'Demon',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 7,
      maxSpawned: 3,
      interval: 200,
      chance: 0.25,
      distance: 10,
      groupSize: [2, 4],
      groupRadius: 15,
      altitude: [1, 1000],
      night: false,
      offset: 20,
      levels: [1, 2],
    }), spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 7,
      maxSpawned: 5,
      interval: 160,
      chance: 0.3,
      distance: 10,
      groupSize: [3, 6],
      groupRadius: 15,
      altitude: [2, 1000],
      night: true,
      offset: 20,
      levels: [1, 3],
    })],
    attacks: single([
      // charred_twitcher_scratch_l
      { dmg: dmg({ slash: 100 }), force: 40, name: 'scratch' },
      // charred_twitcher_scratch_r
      { dmg: dmg({ slash: 100 }), force: 40, name: 'scratch' },
      // charred_twitcher_throw
      { dmg: dmg({ pierce: 75 }), force: 15, name: 'throw' },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: { walk: 2, run: 6, swim: 0 },
    turnSpeed: { walk: 400, run: 300, swim: 0 },
    hp: 220,
    stagger: { factor: 0.5, time: 1.5 },
    damageModifiers: charredDmgModifiers,
    drop: [
      dropEntry('CharredBone', { max: 2 }),
    ],
  }, {
    id: 'Charred_Twitcher_Summoned',
    hp: 50,
  }),
  {
    type: 'creature',
    id: 'staff_greenroots_tentaroot',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 7,
    emoji: '🥦',
    faction: 'Players',
    spawners: [],
    attacks: single([
      // staff_greenroots_tentaroot_attack
      { dmg: dmg({ blunt: 70, chop: 20, pickaxe: 20, poison: 40 }), name: 'poke', force: 40, toolTier: 4 }
      // effect: ImmobilizedAshlands, chance: 0.1
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.SMOKE,
    speed: { walk: 0, run: 0, swim: 0 },
    turnSpeed: { walk: 200, run: 200, swim: 100 },
    hp: 250,
    stagger: null,
    damageModifiers: {
      ...defaultDmgModifiers,
      fire: 'resistant',
      poison: 'immune',
    },
    drop: [],
  },
  {
    type: 'creature',
    id: 'Goblin_Gem',
    disabled: true,
    ragdollId: null,
    components: ['BaseAI', 'Character', 'MonsterAI'],
    tier: 7,
    emoji: '💎',
    faction: 'ForestMonsters',
    spawners: [],
    attacks: single([]),
    tolerate: TOLERATE.WATER,
    speed: { walk: 1.5, run: 12, swim: 2 },
    turnSpeed: { walk: 80, run: 200, swim: 100 },
    hp: 1000,
    stagger: null,
    damageModifiers: animalDmgModifiers,
    drop: [
      dropEntry('GemstoneBlue', { min: 2, max: 4 }),
      dropEntry('GemstoneGreen', { min: 2, max: 4 }),
      dropEntry('GemstoneRed', { min: 2, max: 4 }),
    ],
    timedDestruction: [23, 25],
  },
  {
    type: 'creature',
    group: 'blob',
    id: 'BlobLava',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tier: 7,
    emoji: '🦠',
    faction: 'Demon',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      maxSpawned: 2,
      interval: 260,
      chance: 0.25,
      distance: 10,
      groupSize: [1, 2],
      groupRadius: 1,
      radius: [50, 0],
      altitude: [2, 1000],
      levels: [1, 1],
    })],
    attacks: single([
      // blobLava_attack_aoe
      { dmg: dmg({ blunt: 70, chop: 160, pickaxe: 160, fire: 30 }), name: 'explosion', collider: areaCollider(4) },
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE | TOLERATE.SMOKE,
    speed: { walk: 1, run: 2, swim: 0 },
    turnSpeed: { walk: 100, run: 100, swim: 0 },
    hp: 300,
    stagger: null,
    damageModifiers: mods([2, 0, 1, 4, 4, 3, 2, 2, 1, 0]),
    drop: [
      dropEntry('ProustitePowder', { min: 1, max: 2 }),
      dropEntry('SulfurStone', { min: 1, max: 2 }),
    ],
  },
  {
    type: 'creature',
    id: 'Morgen',
    iconId: 'resource/TrophyMorgen',
    ragdollId: null,
    components: ['Character'],
    tier: 7,
    emoji: '🕷',
    faction: 'Demon',
    spawners: [spawner({
      // Morgen_NonSleeping
      tier: 7,
      biomes: ['Ashlands'],
      maxSpawned: 3,
      interval: 4000,
      chance: 0.1,
      distance: 15,
      radius: [50, 0],
      levels: [1, 1],
    })],
    attacks: single([
      // Morgen_bite
      { dmg: dmg({ pierce: 160, chop: 100, pickaxe: 100 }), name: 'bite', force: 70, toolTier: 3 }, // interval: 4
      // Morgen_roll_right
      { dmg: dmg({ blunt: 40, chop: 100, pickaxe: 100 }), name: 'roll_right', force: 70, toolTier: 3, collider: areaCollider(3) }, // interval: 12
      // Morgen_roll_left
      { dmg: dmg({ blunt: 40, chop: 100, pickaxe: 100 }), name: 'roll_left', force: 70, toolTier: 3, collider: areaCollider(3) }, // interval: 12
      // Morgen_swipe_1
      { dmg: dmg({ pierce: 160, chop: 100, pickaxe: 100 }), name: 'swipe 1', force: 20, toolTier: 3 }, // interval: 5
      // Morgen_swipe_2
      { dmg: dmg({ pierce: 160, chop: 100, pickaxe: 100 }), name: 'swipe 2', force: 20, toolTier: 3 }, // interval: 5
      // Morgen_swipe_3
      { dmg: dmg({ pierce: 160, chop: 100, pickaxe: 100 }), name: 'swipe 3', force: 20, toolTier: 3 }, // interval: 5
      // Morgen_swipe_4
      { dmg: dmg({ pierce: 160, chop: 100, pickaxe: 100 }), name: 'swipe 4', force: 20, toolTier: 3 }, // interval: 5
      // Morgen_bodyslam
      { dmg: dmg({ blunt: 160 }), name: 'bodyslam', force: 70, toolTier: 3 }, // interval: 12
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE,
    speed: { walk: 3, run: 8.6, swim: 4 },
    turnSpeed: { walk: 80, run: 150, swim: 60 },
    hp: 1600,
    stagger: { factor: 0.3, time: 1.5 },
    damageModifiers: mods([1, 1, 1, 4, 4, 1, 0, 2, 0, 0]),
    drop: [
      dropTrophy('TrophyMorgen', 0.05),
      dropEntry('MorgenSinew', { chance: 0.5, min: 1, max: 2 }),
      dropEntry('MorgenHeart', { chance: 0.5 }),
    ],
  },
  {
    type: 'creature',
    id: 'FallenValkyrie',
    iconId: 'resource/TrophyFallenValkyrie',
    ragdollId: null,
    components: ['Character'],
    tags: ['fly'],
    tier: 7,
    emoji: '🦅',
    faction: 'Demon',
    spawners: [spawner({
      tier: 7,
      biomes: ['Ashlands'],
      biomeAreas: 3,
      maxSpawned: 1,
      interval: 4000,
      chance: 0.2,
      distance: 15,
      radius: [50, 0],
      altitude: [15, 1000],
      offset: 20,
      lava: undefined,
      levels: [1, 1],
    })],
    attacks: single([
      // FallenValkyrie_spit
      // -> fallenvalkyrie_spit_projectile x3
      // -> FallenValkyrie_projectile_explosion
      { dmg: dmg({ fire: 80, poison: 18 }), name: 'spit' }, // interval: 10
      // FallenValkyrie_claws
      { dmg: dmg({ slash: 105, pierce: 55 }), name: 'claws' }, // interval: 3
      // FallenValkyrie_spin
      { dmg: dmg({ blunt: 80, pierce: 100, chop: 100, pickaxe: 100 }), name: 'spin', force: 250, collider: areaCollider(5.67) }, // interval: 10
      // FallenValkyrie_taunt
      { dmg: dmg({ slash: 20, pierce: 20 }), name: 'taunt', force: 280 }, // interval: 30
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE,
    speed: { walk: 10, run: 15, swim: 0 },
    turnSpeed: { walk: 300, run: 220, swim: 0 },
    hp: 1500,
    stagger: { factor: 0.5, time: 1.5 },
    damageModifiers: mods([0, 0, 0, 4, 4, 1, 0, 0, 3, 2]),
    drop: [
      dropTrophy('TrophyFallenValkyrie', 0.05),
      dropEntry('CelestialFeather', { chance: 0.5, min: 2, max: 4 }),
    ],
  },
  {
    type: 'creature',
    id: 'Fader',
    iconId: 'resource/TrophyFader',
    ragdollId: null,
    components: ['BaseAI', 'Character', 'Humanoid', 'MonsterAI'],
    tags: ['dragon'],
    tier: 7,
    emoji: '🐉',
    faction: 'Boss',
    spawners: [],
    attacks: single([
      // Fader_Fissure
      { dmg: dmg({ pierce: 120, chop: 1000, pickaxe: 1000 }), name: 'fissure', force: 50, toolTier: 3, aiMinHp: 0.35, aiMaxHp: 0.85 }, // interval: 30, range: [0, 40]
      // Fader_Bite
      { dmg: dmg({ pierce: 210, chop: 300, pickaxe: 300 }), name: 'bite', force: 100, toolTier: 3 }, // interval: 3, range: [0, 9]
      // Fader_Claw_Left
      { dmg: dmg({ pierce: 200, chop: 300, pickaxe: 300 }), name: 'claw-left', force: 100, toolTier: 3 }, // interval: 3, range: [0, 9]
      // Fader_Claw_Right
      { dmg: dmg({ pierce: 200, chop: 300, pickaxe: 300 }), name: 'claw-right', force: 100, toolTier: 3 }, // interval: 3, range: [0, 9]
      // Fader_Spin
      { dmg: dmg({ pierce: 140, chop: 300, pickaxe: 300 }), name: 'spin', force: 130, toolTier: 3, collider: areaCollider(8.5) }, // interval: 20, range: [0, 8]
      // Fader_Flamebreath
      { dmg: dmg({ chop: 40, pickaxe: 40, fire: 60 }), name: 'flamebreath', force: 0, toolTier: 0, aiMinHp: 0.05, aiMaxHp: 0.85 }, // interval: 25, range: [2, 20]
      // Fader_Meteors, dodgeable: false, blockable: false
        // -> spawn_fader_meteors
        // -> projectile_meteor_fader x10
      { dmg: dmg({ blunt: 40, chop: 100, pickaxe: 100, fire: 120 }), name: 'meteors', force: 100, toolTier: 0, aiMinHp: 0.25, aiMaxHp: 1, burst: 10 }, // interval: 25, range: [0, 30]
      // Fader_Roar -> Fader_Roar_Projectile -> Fader_Roar_Spawn
      { spawn: ['Charred_Melee_Fader', 'Charred_Archer_Fader'], name: 'roar', number: [1, 1], max: 7, aiMinHp: 0.35, aiMaxHp: 0.55 }, // interval: 45, range: [0, 100]
      // Fader_WallOfFire
      { dmg: dmg({ pierce: 120, chop: 1000, pickaxe: 1000 }), name: 'wall of fire', force: 50, toolTier: 3, aiMinHp: 0.15, aiMaxHp: 0.9 }, // interval: 60, range: [0, 40]
      // Fader_Meteors_Intense, dodgeable: false, blockable: false
        // -> spawn_fader_meteors
        // -> projectile_meteor_fader x10
      { dmg: dmg({ blunt: 40, chop: 100, pickaxe: 100, fire: 120 }), name: 'meteors', force: 100, toolTier: 0, aiMaxHp: 0.25, burst: 10 }, // interval: 18, range: [0, 30]
      // Fader_Fissure_Intense
        // chop: 30, pickaxe: 30, fire: 80, spirit: 80
        // radius: 4, ttl: 20, hitInterval: 0.5
      { dmg: dmg({ pierce: 120, chop: 1000, pickaxe: 1000 }), name: 'fissure', force: 50, toolTier: 3, aiMaxHp: 0.35 }, // interval: 20, range: [0, 40]
      // Fader_Roar_Intense -> Fader_Roar_Projectile -> Fader_Roar_Spawn
      { spawn: ['Charred_Melee_Fader', 'Charred_Archer_Fader'], name: 'roar', number: [1, 1], max: 7, aiMaxHp: 0.35 }, // interval: 26, range: [0, 100]
    ]),
    tolerate: TOLERATE.WATER | TOLERATE.FIRE,
    speed: { walk: 12, run: 12, swim: 0 },
    turnSpeed: { walk: 120, run: 250, swim: 0, },
    hp: 20000,
    stagger: null,
    damageModifiers: mods([0, 0, 1, 4, 4, 3, 0, 0, 0, 3]),
    drop: [
      dropTrophy('TrophyFader', 1),
      dropEntry('FaderDrop', { min: 3, scale: false }),
    ],
  },
];
